import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { QueueRealtimeEvent } from '../lib/queue/types';
import { AxiosError } from 'axios';
import { useAppDispatch, useAppSelector } from '../store/store';
import { isMultiTabPlayerEnabled, isSpotifyWebPlaybackEnabled } from '../lib/config/featureFlags';
import { loadSpotifyWebPlaybackSdk } from '../lib/spotify/loadSpotifySdk';
import { authService } from '../lib/spotify/auth';
import { spotifyService } from '../lib/spotify/spotifyService';
import { emitPlaybackTelemetry } from '../lib/spotify/playbackTelemetry';
import { authActions } from '../store/slices/auth';
import { spotifyActions } from '../store/slices/spotify';
import { playerService } from '../services/player';
import {
  createQueueSession as createQueueSessionApi,
  fetchQueueSession,
  postQueueAction,
  type BackendError,
  type QueueActionRequest,
  type QueueActionType,
  type QueueActionOrigin,
  type QueueItem,
  type QueueSeedDescriptor,
  type QueueSessionEnvelope,
  type QueueSessionSummary,
} from '../lib/playback/queueApi';
import { useQueueRealtimeSubscription } from '../lib/playback/useQueueRealtimeSubscription';
import {
  useQueueProgressReporter,
  type ProgressSummary,
  type ProgressTriggerOptions
} from '../lib/playback/useQueueProgressReporter';
import { extractQueueItemMetadata } from '../lib/playback/queueDisplay';
import { getSpotifyUserId } from '../lib/auth/spotifyUserId';
import { logPlaybackTimingEvent } from '../lib/debug/playbackTimingDiagnostics';
import { isVerboseLoggingEnabled } from '../lib/debug/logging';
import { createQueueDiagnostics } from '../lib/playback/queueDiagnostics';
import {
  MultiTabCoordinator,
  type ControlPayload,
  type ControlMessage,
  type PlayerStateBroadcast,
} from '../lib/playback/multiTabCoordinator';

const resolveEnvBoolean = (raw: unknown): boolean | undefined => {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (!normalized) return undefined;
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  }
  return undefined;
};

const resolveRuntimeEnvValue = (key: string): unknown => {
  const winEnv = typeof window !== 'undefined' ? (window as any).__ENV : undefined;
  if (winEnv && Object.prototype.hasOwnProperty.call(winEnv, key)) {
    return winEnv[key];
  }
  if (typeof import.meta !== 'undefined' && (import.meta as any).env && key in (import.meta as any).env) {
    return (import.meta as any).env[key];
  }
  if (typeof process !== 'undefined' && process && typeof process.env === 'object') {
    return (process.env as Record<string, string | undefined>)[key];
  }
  return undefined;
};

// Single flag to disable potentially heavy/slow features (realtime, etc.).
// Defaults to ENABLED unless explicitly set to true.
const rawDisableHeavyFeatures = resolveRuntimeEnvValue('REACT_APP_DISABLE_HEAVY_FEATURES');
const DISABLE_HEAVY_FEATURES = resolveEnvBoolean(rawDisableHeavyFeatures) === true;

const isDevelopmentEnv =
  typeof process !== 'undefined'
    ? process.env.NODE_ENV !== 'production'
    : true;
// Logging is further gated by a verbose flag to avoid noisy consoles during routine development.
const isDevelopment = isDevelopmentEnv && isVerboseLoggingEnabled();
const LOG_NAMESPACE = '[SpotifyTokenTrace]';
const AUTO_ADVANCE_LOG_PREFIX = '[AutoAdvance]';
const PLAYBACK_EVENT_PREFIX = '[PlaybackFlow]';
const AUTO_END_DEBOUNCE_MS = 300;
const AUTO_END_SUPPRESS_AFTER_PLAY_MS = 1500;
const AUTO_END_NEAR_DURATION_MS = 4000;
const AUTO_END_POLL_DELAY_MS = 400;
const ENABLE_AUTO_END_POLL =
  typeof import.meta !== 'undefined' &&
  !!import.meta.env?.VITE_ENABLE_PLAYBACK_END_POLL;
const DURATION_FALLBACK_TOLERANCE_MS = 1500;
const PREVIOUS_TRACK_RESTART_THRESHOLD_MS = 5000;
const ENABLE_AUTO_RESUME =
  typeof import.meta !== 'undefined' &&
  !!import.meta.env?.VITE_ENABLE_PLAYBACK_AUTO_RESUME;
const UNEXPECTED_PAUSE_AUTO_RESUME_DELAY_MS = 1500;
const AUTO_RESUME_USER_PAUSE_GRACE_MS = 4000;
const AUTO_RESUME_COOLDOWN_MS = 12000;
const AUTO_RESUME_MIN_POSITION_MS = 1500;
const AUTO_RESUME_END_THRESHOLD_MS = 4000;
const PLAYER_STATE_BROADCAST_INTERVAL_MS = 400;
const PLAYER_STATE_STALE_MS = 4000;
const REMOTE_PROGRESS_SNAP_MS = 900;
const REMOTE_PROGRESS_TICK_MS = 500;
const PLAY_STATE_UI_DEBOUNCE_MS = 100;
const MULTI_TAB_LOG_PREFIX = '[MultiTab]';
const SYNC_LOG_PREFIX = '[PlaybackSync]';
const DEVICE_404_GRACE_MS = 2000;

const maskToken = (token: string | null | undefined): string => {
  if (typeof token !== 'string' || token.length === 0) return 'null';
  return token;
};

const decodeTokenScopes = (token: string): string[] | null => {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');

  try {
    let json: string | null = null;
    if (typeof atob === 'function') {
      json = atob(base64);
    } else if (typeof Buffer !== 'undefined') {
      json = Buffer.from(base64, 'base64').toString('utf8');
    }
    if (!json) return null;
    const payload = JSON.parse(json);
    const scopeField = payload?.scope;
    if (typeof scopeField === 'string') {
      return scopeField.split(' ').filter(Boolean);
    }
    if (Array.isArray(scopeField)) {
      return scopeField.map(String).filter(Boolean);
    }
    return null;
  } catch {
    return null;
  }
};

const traceDebug = (message: string, details?: Record<string, unknown>) => {
  if (isDevelopment) {
    console.log(LOG_NAMESPACE, message, details ?? '');
  }
};

const traceWarn = (message: string, details?: Record<string, unknown>) => {
  console.warn(LOG_NAMESPACE, message, details ?? '');
};

const autoAdvanceLog = (event: string, details?: Record<string, unknown>) => {
  if (isDevelopment) {
    console.log(AUTO_ADVANCE_LOG_PREFIX, event, details ?? {});
  }
};

const RESTRICTION_MESSAGE = 'Restriction violated';

type RestrictedPlaybackErrorMeta = {
  trackUri: string | null;
  pointerIndex: number | null;
  recordingId: string | null;
};

class RestrictedPlaybackError extends Error {
  readonly meta: RestrictedPlaybackErrorMeta;

  constructor(meta: RestrictedPlaybackErrorMeta) {
    super('Restricted playback');
    this.name = 'RestrictedPlaybackError';
    this.meta = meta;
  }
}

const isSpotifyRestrictionError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }
  if (error.response?.status !== 403) {
    return false;
  }
  const payload = error.response?.data;
  let message: string | undefined;
  if (typeof payload === 'string') {
    message = payload;
  } else if (payload && typeof payload === 'object') {
    if (typeof (payload as { message?: string }).message === 'string') {
      message = (payload as { message: string }).message;
    } else if (typeof (payload as { error?: { message?: string } }).error?.message === 'string') {
      message = (payload as { error: { message: string } }).error.message;
    } else {
      try {
        message = JSON.stringify(payload);
      } catch {
        message = undefined;
      }
    }
  }
  return typeof message === 'string' && message.includes(RESTRICTION_MESSAGE);
};

const playbackFlowLog = (event: string, details?: Record<string, unknown>) => {
  if (isDevelopment) {
    console.log(`${PLAYBACK_EVENT_PREFIX}`, event, details ?? {});
  }
};

type WakeLockSentinel = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener?: (type: 'release', listener: () => void) => void;
};

type AutoAdvanceReason = 'paused-zero' | 'uri-changed';

const normalizeTrackUri = (value: string | null | undefined): string => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const resolveQueueItemTrackUri = (item: QueueItem | null | undefined): string | null => {
  if (!item) return null;
  const resolved = normalizeTrackUri((item as QueueItem).resolvedTrackUri);
  if (resolved) return resolved;
  const canonical = normalizeTrackUri(item.trackUri);
  return canonical || null;
};

const queueItemMatchesUri = (item: QueueItem | null | undefined, uri: string): boolean => {
  if (!item) return false;
  if (!uri) return false;
  const canonical = normalizeTrackUri(item.trackUri);
  if (canonical && canonical === uri) return true;
  const resolved = normalizeTrackUri(item.resolvedTrackUri);
  return Boolean(resolved && resolved === uri);
};

const getPointerFromSession = (
  session: QueueSessionSummary | null,
  items: QueueItem[]
): { pointerItem: QueueItem | null; pointerIndex: number | null } => {
  if (!session || !items.length) {
    return { pointerItem: null, pointerIndex: null };
  }

  if (session.currentItemId) {
    const byIdIndex = items.findIndex((item) => item.id === session.currentItemId);
    if (byIdIndex !== -1) {
      return { pointerItem: items[byIdIndex] ?? null, pointerIndex: byIdIndex };
    }
  }

  const index =
    typeof session.currentItemIndex === 'number' &&
    Number.isFinite(session.currentItemIndex) &&
    session.currentItemIndex >= 0 &&
    session.currentItemIndex < items.length
      ? session.currentItemIndex
      : null;

  return {
    pointerItem: index != null ? items[index] ?? null : null,
    pointerIndex: index,
  };
};

const findQueueIndexByUri = (items: QueueItem[], uri: string | null | undefined): number | null => {
  const normalized = normalizeTrackUri(uri);
  if (!normalized) return null;
  const matchIndex = items.findIndex((item) => queueItemMatchesUri(item, normalized));
  return matchIndex >= 0 ? matchIndex : null;
};

const deriveRecordingKey = (item: QueueItem | undefined, fallback: string): string => {
  if (!item) {
    return fallback;
  }
  if (item.groupType === 'recording' && typeof item.groupId === 'string' && item.groupId.trim().length > 0) {
    return item.groupId.trim();
  }
  const metadata = item.metadata ?? {};
  const rawRecordingId = (metadata as Record<string, unknown>)['recordingId'];
  if (typeof rawRecordingId === 'string' && rawRecordingId.trim().length > 0) {
    return rawRecordingId.trim();
  }
  if (typeof item.groupId === 'string' && item.groupId.trim().length > 0) {
    return item.groupId.trim();
  }
  return item.id ?? fallback;
};

const findPreviousRecordingStartIndex = (items: QueueItem[], pointerIndex: number | null): number | null => {
  if (pointerIndex == null || pointerIndex <= 0 || pointerIndex >= items.length) {
    return null;
  }

  const currentKey = deriveRecordingKey(items[pointerIndex], `queue-index-${pointerIndex}`);
  for (let index = pointerIndex - 1; index >= 0; index -= 1) {
    const candidateKey = deriveRecordingKey(items[index], `queue-index-${index}`);
    if (candidateKey !== currentKey) {
      let start = index;
      while (start - 1 >= 0) {
        const prevKey = deriveRecordingKey(items[start - 1], `queue-index-${start - 1}`);
        if (prevKey !== candidateKey) {
          break;
        }
        start -= 1;
      }
      return start;
    }
  }
  return null;
};

type PlaybackIntent = {
  seq: number;
  uri: string;
  index: number | null;
  started: boolean;
  firstSeenAt: number | null;
  resolutionRequested: boolean;
};

type PlaybackSnapshot = {
  seq: number;
  uri: string;
  index: number | null;
  startedAt: number;
};

type PlaybackStatus = 'idle' | 'initializing' | 'ready' | 'error' | 'unauthenticated';
type PlayerState = Spotify.PlaybackState | null;
type QueueStateStatus = 'idle' | 'hydrating' | 'ready';

type QueueState = {
  session: QueueSessionSummary | null;
  items: QueueItem[];
  status: QueueStateStatus;
  error: string | null;
  lastUpdatedAt: number | null;
};

type PlayTrackOptions = {
  queueSeed?: QueueSeedDescriptor | null;
  targetTrackUri?: string | null;
};

type QueueActionExtras = {
  targetIndex?: number;
  targetItemIds?: string[];
  targetGroupId?: string;
  deviceId?: string;
  sourceIndex?: number;
  groupId?: string;
  targetGroupIndex?: number;
  origin?: QueueActionOrigin;
  debugContext?: Record<string, unknown>;
  recordingIds?: string[];
  trackUris?: string[];
  trackDurationsMs?: Array<number | null>;
  trackMetadata?: Array<Record<string, unknown>>;
};

const PLAYBACK_SYNC_ACTIONS: readonly QueueActionType[] = [
  'next-track',
  'previous-track',
  'jump-to-index',
  'skip-recording',
];

type PlaybackContextType = {
  status: PlaybackStatus;
  error: string | null;
  deviceId: string | null;
  player: Spotify.Player | null;
  playerState: PlayerState;
  currentTrackId: string | null;
  isPlaying: boolean;
  isPlayingDisplay: boolean;
  volumePercent: number | null;
  queueSession: QueueSessionSummary | null;
  queueItems: QueueItem[];
  queueStatus: QueueStateStatus;
  queueError: string | null;
  role: LeaderFollowerRole;
  isLeader: boolean;
  leaderSessionId: string | null;
  multiTabEnabled: boolean;
  hydrateQueueSession: () => Promise<void>;
  clearQueueSession: () => void;
  performQueueAction: (action: QueueActionType, extras?: QueueActionExtras) => Promise<void>;
  playTrack: (trackId: string, options?: PlayTrackOptions) => Promise<void>;
  pauseTrack: () => Promise<void>;
  resumeTrack: (
    options?: { session?: QueueSessionSummary | null; items?: QueueItem[]; positionMs?: number }
  ) => Promise<void>;
  seekToPosition: (positionMs: number) => Promise<void>;
  setVolumePercent: (volumePercent: number, options?: { userInitiated?: boolean; source?: string }) => Promise<void>;
  toggleMute: () => Promise<void>;
  nextTrack: () => Promise<void>;
  previousTrack: () => Promise<void>;
  skipRecording: () => Promise<void>;
  stopPlayback: () => Promise<void>;
  toggleShuffle: (enabled?: boolean) => Promise<void>;
  setRepeatMode: (mode: 'track' | 'context' | 'off') => Promise<void>;
  startPlayback: (
    body?: {
      context_uri?: string;
      uris?: string[];
      offset?: { position: number };
      position_ms?: number;
    }
  ) => Promise<void>;
};

type PerformQueueActionFn = (action: QueueActionType, extras?: QueueActionExtras) => Promise<void>;
type LeaderFollowerRole = 'leader' | 'follower' | 'unknown';
type RemoteStateSnapshot = PlayerStateBroadcast & { appliedAt: number };

const INITIAL_QUEUE_STATE: QueueState = {
  session: null,
  items: [],
  status: 'idle',
  error: null,
  lastUpdatedAt: null,
};

const PROGRESS_HEARTBEAT_INTERVAL_MS = 12_000;
const PLAYER_STATE_POLL_INTERVAL_MS = 1_000;
const INITIAL_CONTEXT: PlaybackContextType = {
  status: 'idle',
  error: null,
  deviceId: null,
  player: null,
  playerState: null,
  currentTrackId: null,
  isPlaying: false,
  isPlayingDisplay: false,
  volumePercent: null,
  queueSession: null,
  queueItems: [],
  queueStatus: 'idle',
  queueError: null,
  role: 'leader',
  isLeader: true,
  leaderSessionId: null,
  multiTabEnabled: false,
  hydrateQueueSession: async () => {
    throw new Error('Playback provider not ready');
  },
  clearQueueSession: () => {
    throw new Error('Playback provider not ready');
  },
  performQueueAction: async () => {
    throw new Error('Playback provider not ready');
  },
  playTrack: async () => {
    throw new Error('Playback provider not ready');
  },
  pauseTrack: async () => {
    throw new Error('Playback provider not ready');
  },
  resumeTrack: async () => {
    throw new Error('Playback provider not ready');
  },
  seekToPosition: async () => {
    throw new Error('Playback provider not ready');
  },
  setVolumePercent: async (_volumePercent: number, _options?: { userInitiated?: boolean; source?: string }) => {
    throw new Error('Playback provider not ready');
  },
  toggleMute: async () => {
    throw new Error('Playback provider not ready');
  },
  nextTrack: async () => {
    throw new Error('Playback provider not ready');
  },
  previousTrack: async () => {
    throw new Error('Playback provider not ready');
  },
  skipRecording: async () => {
    throw new Error('Playback provider not ready');
  },
  stopPlayback: async () => {
    throw new Error('Playback provider not ready');
  },
  toggleShuffle: async () => {
    throw new Error('Playback provider not ready');
  },
  setRepeatMode: async () => {
    throw new Error('Playback provider not ready');
  },
  startPlayback: async () => {
    throw new Error('Playback provider not ready');
  },
};

const PlaybackContext = createContext<PlaybackContextType>(INITIAL_CONTEXT);

interface PlaybackProviderProps {
  children: ReactNode;
}

const ensureSpotifyTrackUri = (trackId: string): string =>
  trackId.startsWith('spotify:track:') ? trackId : `spotify:track:${trackId}`;

const resolveTrackIdFromUri = (trackUri: string | null | undefined): string | null => {
  if (!trackUri) return null;
  return trackUri.startsWith('spotify:track:') ? trackUri.slice('spotify:track:'.length) : trackUri;
};

const deriveQueueSeed = (trackId: string, seed?: QueueSeedDescriptor | null): QueueSeedDescriptor => {
  if (seed) {
    return seed;
  }

  return {
    type: 'manual-tracks',
    trackUris: [ensureSpotifyTrackUri(trackId)],
  };
};

export const PlaybackProvider = ({ children }: PlaybackProviderProps) => {
  const isAuthenticated = useAppSelector((state) => !!state.auth.user);
  const dispatch = useAppDispatch();
  const multiTabEnabled = isMultiTabPlayerEnabled();

  const [status, setStatus] = useState<PlaybackStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [playerState, setPlayerState] = useState<PlayerState>(null);
  const [currentTrackId, setCurrentTrackId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayingDisplay, setIsPlayingDisplay] = useState(false);
  const [volumePercent, setVolumePercentState] = useState<number | null>(null);
  const [queueState, setQueueState] = useState<QueueState>(INITIAL_QUEUE_STATE);
  const [localPlaybackAuthority, setLocalPlaybackAuthority] = useState(!multiTabEnabled);
  const [role, setRole] = useState<LeaderFollowerRole>(multiTabEnabled ? 'unknown' : 'leader');
  const [leaderSessionId, setLeaderSessionId] = useState<string | null>(null);
  const [multiTabReady, setMultiTabReady] = useState(!multiTabEnabled);

  const isLeader = !multiTabEnabled || role === 'leader';

  const playerRef = useRef<Spotify.Player | null>(null);
  const playerStatePollingRef = useRef<number | null>(null);
  const playerStatePollInFlightRef = useRef(false);
  const playerPrimedRef = useRef(false);
  const playbackCommandLockRef = useRef<Promise<void>>(Promise.resolve());
  const playSequenceRef = useRef(0);
  const currentIntentRef = useRef<PlaybackIntent | null>(null);
  const currentPlayingRef = useRef<PlaybackSnapshot | null>(null);
  const endHandledSeqRef = useRef<number | null>(null);
  const suppressEndUntilRef = useRef<number>(0);
  const armedSeqRef = useRef<number | null>(null);
  const deviceIdRef = useRef<string | null>(null);
  const lastNonZeroVolumeRef = useRef<number>(50);
  const deviceWaitersRef = useRef<Set<{ resolve: (id: string) => void; reject: (error: Error) => void }>>(
    new Set()
  );
  const playerStateRef = useRef<Spotify.PlaybackState | null>(null);
  const previousTrackIdRef = useRef<string | null>(null);
  const queueRevisionRef = useRef<number | null>(null);
  const queueHydratePromiseRef = useRef<Promise<void> | null>(null);
  const queueActionInFlightRef = useRef(false);
  const queueSessionRef = useRef<QueueSessionSummary | null>(null);
  const queueItemsRef = useRef<QueueItem[]>([]);
  const performQueueActionRef = useRef<PerformQueueActionFn | null>(null);
  const getLastNonZeroProgressSummaryRef = useRef<() => ProgressSummary | null>(() => null);
  const getLastNonZeroProgressUpdatedAtRef = useRef<() => number>(() => 0);
  const lastPlayingStateRef = useRef<{
    positionMs: number;
    durationMs: number | null;
    trackUri: string | null;
    updatedAt: number;
  } | null>(null);
  const isLeaderRef = useRef<boolean>(!multiTabEnabled);
  const multiTabCoordinatorRef = useRef<MultiTabCoordinator | null>(null);
  const remoteStateSeqRef = useRef<number>(0);
  const remoteStateLeaderRef = useRef<string | null>(null);
  const remoteStateRef = useRef<RemoteStateSnapshot | null>(null);
  const remoteProgressTimerRef = useRef<number | null>(null);
  const lastPlayerBroadcastAtRef = useRef<number>(0);
  const playStateDisplayRef = useRef<{ value: boolean; source: string; updatedAt: number }>({
    value: false,
    source: 'init',
    updatedAt: 0,
  });
  const playStateDisplayTimerRef = useRef<number | null>(null);
  const endCandidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingEndCandidateRef = useRef<{ seq: number; reason: AutoAdvanceReason } | null>(null);
  const pendingIntentResolutionSeqRef = useRef<number | null>(null);
  const requestPlayerStateRef = useRef<(source: 'poll' | 'sync') => void>(() => {});
  const wakeLockSentinelRef = useRef<WakeLockSentinel | null>(null);
  const wakeLockRequestedRef = useRef(false);
  const resolvedTrackNotifierRef = useRef<
    (payload: { itemId: string; trackUri: string; source: string }) => void
  >(() => {});
  const resolvedTrackUriCacheRef = useRef<Map<string, string>>(new Map());
  const controlHandlerRef = useRef<
    (message: ControlMessage) => Promise<{ ok: boolean; error?: string }>
  >(async () => ({ ok: false, error: 'uninitialized' }));
  const lastPauseIntentAtRef = useRef<number>(0);
  const pauseIntentActiveRef = useRef(false);
  const autoResumeTimerRef = useRef<number | null>(null);
  const lastAutoResumeAttemptAtRef = useRef<number>(0);
  const unloadHandlerAttachedRef = useRef(false);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const localPlaybackAuthorityRef = useRef(localPlaybackAuthority);
  const sendQueueProgressRawRef = useRef<((options?: ProgressTriggerOptions) => Promise<boolean>) | null>(
    null
  );

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    localPlaybackAuthorityRef.current = localPlaybackAuthority;
  }, [localPlaybackAuthority]);

  const grantLocalPlaybackAuthority = useCallback(() => {
    setLocalPlaybackAuthority(true);
  }, []);

  const clearEndCandidateTimer = useCallback(() => {
    if (endCandidateTimerRef.current != null) {
      clearTimeout(endCandidateTimerRef.current);
      endCandidateTimerRef.current = null;
    }
    pendingEndCandidateRef.current = null;
  }, []);

  const clearAutoResumeTimer = useCallback(() => {
    if (autoResumeTimerRef.current != null) {
      if (typeof window !== 'undefined') {
        window.clearTimeout(autoResumeTimerRef.current);
      }
      autoResumeTimerRef.current = null;
    }
  }, []);

  const stopRemoteProgressTimer = useCallback(() => {
    if (remoteProgressTimerRef.current != null && typeof window !== 'undefined') {
      window.clearInterval(remoteProgressTimerRef.current);
    }
    remoteProgressTimerRef.current = null;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLocalPlaybackAuthority(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    isLeaderRef.current = isLeader;
  }, [isLeader]);

  useEffect(() => {
    if (isLeader) {
      stopRemoteProgressTimer();
      remoteStateRef.current = null;
      remoteStateSeqRef.current = 0;
      remoteStateLeaderRef.current = null;
    }
  }, [isLeader, stopRemoteProgressTimer]);

  const startRemoteProgressTimer = useCallback(() => {
    stopRemoteProgressTimer();
    if (isLeaderRef.current) return;
    if (typeof window === 'undefined') return;
    const snapshot = remoteStateRef.current;
    if (!snapshot || snapshot.paused) return;

    remoteProgressTimerRef.current = window.setInterval(() => {
      const current = remoteStateRef.current;
      if (!current || current.paused || isLeaderRef.current) {
        stopRemoteProgressTimer();
        return;
      }
      const duration = current.durationMs ?? null;
      const now = Date.now();
      const elapsed = Math.max(0, now - current.appliedAt);
      const nextPosition = current.positionMs + elapsed;
      const clamped = duration != null ? Math.min(nextPosition, duration) : nextPosition;
      remoteStateRef.current = {
        ...current,
        positionMs: clamped,
        appliedAt: now,
      };
      setPlayerState((prev) =>
        prev
          ? ({
              ...prev,
              position: clamped,
            } as Spotify.PlaybackState)
          : prev
      );
      playerStateRef.current = playerStateRef.current
        ? ({
            ...playerStateRef.current,
            position: clamped,
          } as Spotify.PlaybackState)
        : playerStateRef.current;
    }, REMOTE_PROGRESS_TICK_MS);
  }, [stopRemoteProgressTimer]);

  const applySyntheticPlayerState = useCallback(
    (payload: PlayerStateBroadcast, opts?: { allowLeader?: boolean; source?: string }) => {
      if (isLeaderRef.current && !opts?.allowLeader) {
        return;
      }
      const leaderId = payload.leaderSessionId ?? null;
      if (!opts?.allowLeader) {
        if (leaderId && leaderId !== remoteStateLeaderRef.current) {
          remoteStateSeqRef.current = 0;
          remoteStateLeaderRef.current = leaderId;
        }
        const seq = payload.seq ?? 0;
        if (seq && seq <= remoteStateSeqRef.current) {
          return;
        }
        remoteStateSeqRef.current = seq;
        if (leaderId) {
          setLeaderSessionId(leaderId);
        }
        const sentAt = payload.sentAt ?? Date.now();
        if (sentAt && Date.now() - sentAt > PLAYER_STATE_STALE_MS) {
          return;
        }
      }

      const now = Date.now();
      const drift = payload.paused ? 0 : Math.max(0, now - (payload.sentAt ?? now));
      const basePosition = Math.max(0, Math.floor(payload.positionMs ?? 0));
      const effectivePosition = payload.paused ? basePosition : basePosition + drift;
      const durationMs = payload.durationMs ?? null;
      const normalizedPositionRaw =
        durationMs != null ? Math.min(effectivePosition, durationMs) : effectivePosition;
      const previousRemote = remoteStateRef.current;
      let normalizedPosition = normalizedPositionRaw;
      if (
        previousRemote &&
        normalizeTrackUri(previousRemote.trackUri ?? '') === normalizeTrackUri(payload.trackUri ?? '')
      ) {
        const simulatedPosition = previousRemote.paused
          ? previousRemote.positionMs
          : previousRemote.positionMs + Math.max(0, now - previousRemote.appliedAt);
        const delta = Math.abs(simulatedPosition - normalizedPositionRaw);
        if (delta < REMOTE_PROGRESS_SNAP_MS) {
          normalizedPosition = simulatedPosition;
        }
      }
      const repeatMode =
        payload.repeatMode === 'track'
          ? 1
          : payload.repeatMode === 'context'
            ? 2
            : 0;
      const volumePct =
        typeof payload.volume === 'number' && Number.isFinite(payload.volume)
          ? Math.round(Math.max(0, Math.min(1, payload.volume)) * 100)
          : payload.volume != null && Number.isFinite(payload.volume)
            ? Math.round(payload.volume)
            : null;
      const payloadDeviceId = payload.deviceId ?? null;
      const currentDeviceId = deviceIdRef.current;
      const shouldApplyVolume =
        !(isLeaderRef.current && opts?.allowLeader) ||
        (payloadDeviceId && currentDeviceId && payloadDeviceId === currentDeviceId);
      if (volumePct != null) {
        const volumeFromDifferentDevice =
          payloadDeviceId && currentDeviceId && payloadDeviceId !== currentDeviceId;
        if (!shouldApplyVolume) {
          console.info(`${MULTI_TAB_LOG_PREFIX} volume-skip-foreign-device`, {
            volumePct,
            payloadDeviceId,
            currentDeviceId,
            source: opts?.source ?? 'unknown',
            reason: volumeFromDifferentDevice ? 'device-mismatch' : 'device-unknown',
            allowLeader: opts?.allowLeader ?? false,
            isLeader: isLeaderRef.current,
          });
        } else {
          setVolumePercentState(volumePct);
          console.info(`${MULTI_TAB_LOG_PREFIX} volume-applied`, {
            volumePct,
            payloadDeviceId,
            currentDeviceId,
            source: opts?.source ?? 'unknown',
            allowLeader: opts?.allowLeader ?? false,
            isLeader: isLeaderRef.current,
          });
        }
      }

      const syntheticTrack = {
        uri: payload.trackUri ?? '',
        id: resolveTrackIdFromUri(payload.trackUri) ?? payload.trackUri ?? '',
        name: payload.trackName ?? payload.albumName ?? '',
        duration_ms: durationMs ?? 0,
        artists:
          payload.artists?.map((artist) => ({
            name: artist.name,
            uri: artist.id ? `spotify:artist:${artist.id}` : undefined,
            id: artist.id ?? undefined,
          })) ?? [],
        album: {
          name: payload.albumName ?? '',
          uri: '',
          images: [],
        },
      };

      const playbackId = (payload as any).playback_id;
      const syntheticState = {
        context: null,
        disallows: {},
        paused: payload.paused,
        position: normalizedPosition,
        duration: durationMs ?? 0,
        shuffle: payload.shuffle ?? null,
        repeat_mode: repeatMode,
        timestamp: now,
        track_window: {
          current_track: syntheticTrack as any,
          previous_tracks: [],
          next_tracks: [],
        },
        playback_id: playbackId,
        playback_features: (payload as any).playback_features ?? {},
        playback_quality: (payload as any).playback_quality ?? 'standard',
        restrictions: (payload as any).restrictions ?? {},
        loading: false,
      } as unknown as Spotify.PlaybackState;

      if (volumePct != null && shouldApplyVolume) {
        (syntheticState as any).device = { volume_percent: volumePct };
      }

      const nextDeviceId = payload.deviceId ?? null;
      const shouldUpdateDeviceId =
        !(isLeaderRef.current && opts?.allowLeader) ||
        !currentDeviceId ||
        (nextDeviceId && currentDeviceId === nextDeviceId);
      if (shouldUpdateDeviceId) {
        console.info(`${MULTI_TAB_LOG_PREFIX} device-update`, {
          nextDeviceId,
          currentDeviceId,
          source: opts?.source ?? 'unknown',
          allowLeader: opts?.allowLeader ?? false,
          isLeader: isLeaderRef.current,
        });
        setDeviceId(nextDeviceId);
        deviceIdRef.current = nextDeviceId;
      } else if (isDevelopment) {
        console.info(`${MULTI_TAB_LOG_PREFIX} device-skip-foreign`, {
          payloadDeviceId,
          currentDeviceId,
          source: opts?.source ?? 'unknown',
        });
      }
      setPlayerState(syntheticState);
      playerStateRef.current = syntheticState;
      setIsPlaying(!payload.paused);
      setCurrentTrackId(resolveTrackIdFromUri(payload.trackUri));
      setStatus('ready');
      setError(null);
      dispatch(spotifyActions.syncPlaybackState({ state: syntheticState }));

      remoteStateRef.current = {
        ...payload,
        positionMs: normalizedPosition,
        appliedAt: now,
      };
      if (!opts?.allowLeader) {
        startRemoteProgressTimer();
      }
      if (isDevelopment) {
        console.info(`${SYNC_LOG_PREFIX} synthetic-state-applied`, {
          allowLeader: opts?.allowLeader ?? false,
          source: opts?.source ?? 'unknown',
          trackUri: payload.trackUri ?? null,
          positionMs: normalizedPosition,
          paused: payload.paused,
        });
      }
    },
    [dispatch, startRemoteProgressTimer]
  );

  const applyRemotePlayerState = useCallback(
    (payload: PlayerStateBroadcast) => {
      applySyntheticPlayerState(payload, { allowLeader: false, source: 'remote' });
    },
    [applySyntheticPlayerState]
  );

  const promoteToLeader = useCallback(
    (reason: string) => {
      const coordinator = multiTabCoordinatorRef.current;
      coordinator?.promoteToLeader(reason);
      setRole('leader');
      setMultiTabReady(true);
      isLeaderRef.current = true;
      stopRemoteProgressTimer();
    },
    [stopRemoteProgressTimer]
  );

  const sendControlToLeader = useCallback(
    async (action: ControlPayload): Promise<{ ok: boolean; timeout?: boolean; error?: string }> => {
      if (!multiTabEnabled || isLeaderRef.current) {
        return { ok: false, timeout: true };
      }
      const coordinator = multiTabCoordinatorRef.current;
      if (!coordinator) {
        return { ok: false, timeout: true };
      }
      if (action.kind === 'playback' && action.action === 'set-volume') {
        const tabId = coordinator.getTabId();
        const leaderTabId = coordinator.getLeaderTabId();
        console.info(`${MULTI_TAB_LOG_PREFIX} control:send`, {
          kind: action.kind,
          action: action.action,
          payload: action.payload ?? null,
          tabId,
          leaderTabId,
        });
      }
      return coordinator.sendControl(action);
    },
    [multiTabEnabled]
  );

  const armPlaybackIntent = useCallback(
    (
      intent: PlaybackIntent,
      resolvedUri: string | null,
      reason: string,
      meta: {
        source: string;
        pointerIndex: number | null;
        pointerDurationMs?: number | null;
        trackDurationMs?: number | null;
        trackTitle?: string | null;
      } = {
        source: 'unknown',
        pointerIndex: intent.index,
      }
    ) => {
      const now = Date.now();
      intent.started = true;
      intent.resolutionRequested = false;
      currentIntentRef.current = intent;
      currentPlayingRef.current = {
        seq: intent.seq,
        uri: resolvedUri ?? intent.uri,
        index: intent.index,
        startedAt: now,
      };
      endHandledSeqRef.current = null;
      suppressEndUntilRef.current = now + AUTO_END_SUPPRESS_AFTER_PLAY_MS;
      armedSeqRef.current = intent.seq;
      clearEndCandidateTimer();
      if (pendingIntentResolutionSeqRef.current === intent.seq) {
        pendingIntentResolutionSeqRef.current = null;
      }
      autoAdvanceLog('play-armed', {
        seq: intent.seq,
        uri: resolvedUri ?? intent.uri,
        reason,
        source: meta.source,
        pointerIndex: intent.index,
        pointerDurationMs: meta.pointerDurationMs ?? null,
        trackDurationMs: meta.trackDurationMs ?? null,
        trackTitle: meta.trackTitle ?? null,
      });
    },
    [clearEndCandidateTimer]
  );

const tryArmIntentWithTrack = useCallback(
  (
    intent: PlaybackIntent,
    trackInfo: {
      trackUri: string | null;
      linkedFromUri: string | null;
      durationMs: number | null;
      trackTitle?: string | null;
    },
    options: { source: string; allowDurationFallback?: boolean }
  ): boolean => {
    if (!intent || intent.started) {
      return false;
    }
    const items = queueItemsRef.current;
    const { item: queueItem, index } = getQueueItemForIntent(intent, items);
    if (index != null && index >= 0) {
      intent.index = index;
    }
    const pointerDurationMs = queueItem?.durationMs ?? null;
    const trackUri = trackInfo.trackUri;
    const linkedFromUri = trackInfo.linkedFromUri;
    const trackDurationMs = trackInfo.durationMs;
    const queueItemId = queueItem?.id ?? null;
    const allowDurationFallback = options.allowDurationFallback ?? true;
    let pointerTrackTitle: string | null = null;
    if (queueItem?.metadata && typeof queueItem.metadata === 'object') {
      const rawTitle = (queueItem.metadata as Record<string, unknown>).title;
      pointerTrackTitle = typeof rawTitle === 'string' ? rawTitle : null;
    }
    const candidateTrackTitle =
      typeof trackInfo.trackTitle === 'string' ? trackInfo.trackTitle : null;

    if (trackUri && trackUri === intent.uri) {
      armPlaybackIntent(intent, trackUri, 'direct', {
        source: options.source,
        pointerIndex: intent.index,
        pointerDurationMs,
        trackDurationMs,
        trackTitle: pointerTrackTitle,
      });
      return true;
    }

    if (linkedFromUri && linkedFromUri === intent.uri) {
      armPlaybackIntent(intent, trackUri ?? linkedFromUri, 'linked-from', {
        source: options.source,
        pointerIndex: intent.index,
        pointerDurationMs,
        trackDurationMs,
        trackTitle: pointerTrackTitle,
      });
      if (queueItemId && (trackUri ?? linkedFromUri)) {
        resolvedTrackNotifierRef.current({
          itemId: queueItemId,
          trackUri: trackUri ?? linkedFromUri,
          source: 'linked-from',
        });
      }
      return true;
    }

    if (allowDurationFallback) {
      const deltaMs =
        pointerDurationMs != null && trackDurationMs != null
          ? Math.abs(pointerDurationMs - trackDurationMs)
          : null;
      autoAdvanceLog('play-duration-fallback-check', {
        seq: intent.seq,
        pointerDurationMs,
        trackDurationMs,
        source: options.source,
        hasPointerDuration: pointerDurationMs != null,
        hasTrackDuration: trackDurationMs != null,
        deltaMs,
        withinTolerance: Boolean(deltaMs != null && deltaMs <= DURATION_FALLBACK_TOLERANCE_MS),
        pointerTrackTitle,
        pointerTrackUri: queueItem?.trackUri ?? null,
        candidateTrackTitle,
        candidateTrackUri: trackUri ?? linkedFromUri ?? null,
      });

      if (
        pointerDurationMs != null &&
        trackDurationMs != null &&
        Math.abs(pointerDurationMs - trackDurationMs) <= DURATION_FALLBACK_TOLERANCE_MS
      ) {
        autoAdvanceLog('play-duration-fallback', {
          seq: intent.seq,
          pointerDurationMs,
          trackDurationMs,
          source: options.source,
          deltaMs: Math.abs(pointerDurationMs - trackDurationMs),
          pointerTrackTitle,
          pointerTrackUri: queueItem?.trackUri ?? null,
          candidateTrackTitle,
          candidateTrackUri: trackUri ?? intent.uri,
        });
        armPlaybackIntent(intent, trackUri ?? intent.uri, 'duration-fallback', {
          source: options.source,
          pointerIndex: intent.index,
          pointerDurationMs,
          trackDurationMs,
          trackTitle: pointerTrackTitle,
        });
        if (queueItemId && (trackUri ?? intent.uri)) {
          resolvedTrackNotifierRef.current({
            itemId: queueItemId,
            trackUri: trackUri ?? intent.uri,
            source: 'duration-fallback',
          });
        }
        return true;
      }

      autoAdvanceLog('play-duration-fallback-skip', {
        seq: intent.seq,
        pointerDurationMs,
        trackDurationMs,
        source: options.source,
        reason:
          pointerDurationMs == null || trackDurationMs == null
            ? 'missing-duration'
            : 'delta-too-large',
        deltaMs,
        pointerTrackTitle,
        pointerTrackUri: queueItem?.trackUri ?? null,
        candidateTrackTitle,
        candidateTrackUri: trackUri ?? linkedFromUri ?? null,
      });
    }

    return false;
  },
  [armPlaybackIntent]
);

  const queueDiagnostics = useMemo(
    () =>
      createQueueDiagnostics({
        enabled: isDevelopment,
        getSnapshot: () => ({
          session: queueSessionRef.current,
          items: queueItemsRef.current,
        }),
        getProgressSummary: () => {
          const getter = getLastNonZeroProgressSummaryRef.current;
          return getter ? getter() : null;
        },
        getProgressUpdatedAt: () => {
          const getter = getLastNonZeroProgressUpdatedAtRef.current;
          return getter ? getter() : 0;
        },
        getLastPlayingState: () => lastPlayingStateRef.current,
      }),
    []
  );

  const updateQueueItemResolvedUri = useCallback(
    (itemId: string | null, trackUri: string | null) => {
      if (!itemId) return;
      const normalizedUri = normalizeTrackUri(trackUri);
      if (!normalizedUri) return;

      setQueueState((prev) => {
        if (!prev.items.length) {
          return prev;
        }
        let changed = false;
        const nextItems = prev.items.map((item) => {
          if (item.id !== itemId) {
            return item;
          }
          const currentNormalized = normalizeTrackUri(item.resolvedTrackUri ?? item.trackUri);
          if (currentNormalized === normalizedUri) {
            return item;
          }
          changed = true;
          return { ...item, resolvedTrackUri: normalizedUri };
        });
        if (!changed) {
          return prev;
        }
        return {
          ...prev,
          items: nextItems,
        };
      });
      resolvedTrackUriCacheRef.current.set(itemId, normalizedUri);
    },
    [setQueueState]
  );

  const resetAutoAdvanceState = useCallback(
    (reason: string = 'unspecified') => {
      lastPlayingStateRef.current = null;
      armedSeqRef.current = null;
      endHandledSeqRef.current = null;
      clearEndCandidateTimer();
      autoAdvanceLog('state-reset', { reason });
    },
    [clearEndCandidateTimer]
  );

  const queueUserId = getSpotifyUserId();
  const webPlaybackEnabled = isSpotifyWebPlaybackEnabled();

  const notifyStaleQueue = useCallback(() => {
    if (isDevelopment) {
      console.info('[Queue] Session marked stale; notifying user.');
    }
  }, []);

  useEffect(() => {
    if (!multiTabEnabled || typeof window === 'undefined') {
      setRole('leader');
      setLeaderSessionId(null);
      setMultiTabReady(true);
      return;
    }

    const coordinator = new MultiTabCoordinator({
      onRoleChange: (nextRole, info) => {
        setRole(nextRole);
        setLeaderSessionId(info.leaderSessionId);
        if (nextRole !== 'unknown') {
          setMultiTabReady(true);
        }
        if (nextRole === 'follower') {
          remoteStateSeqRef.current = 0;
          remoteStateLeaderRef.current = info.leaderSessionId ?? null;
          setLocalPlaybackAuthority(false);
        }
      },
      onDeviceReady: (id, session) => {
        if (isLeaderRef.current) return;
        setLeaderSessionId(session ?? null);
        setDeviceId(id);
        deviceIdRef.current = id;
        setStatus('ready');
        setError(null);
      },
      onPlayerState: (state) => {
        if (isLeaderRef.current) return;
        applyRemotePlayerState(state);
      },
      onControl: (message) => controlHandlerRef.current(message),
      onLeaderHint: () => {
        setRole('unknown');
        setMultiTabReady(false);
      },
    });

    multiTabCoordinatorRef.current = coordinator;
    
    // Start SDK loading immediately, in parallel with election
    // This eliminates the React re-render delay by starting SDK load
    // at the same time as election, not waiting for state updates
    if (isAuthenticated && webPlaybackEnabled) {
      console.info('[DeviceInitTiming] Early SDK load triggered (parallel with election)', {
        timestamp: new Date().toISOString(),
      });
      void loadSpotifyWebPlaybackSdk().catch(() => {
        // Silently fail - will retry when player initialization runs
      });
    }
    
    coordinator.start();

    return () => {
      if (isLeaderRef.current) {
        coordinator.leaderGoingAway();
      }
      coordinator.destroy();
      multiTabCoordinatorRef.current = null;
      stopRemoteProgressTimer();
    };
  }, [applyRemotePlayerState, multiTabEnabled, stopRemoteProgressTimer]);

  // Start loading SDK early, in parallel with election - don't wait for multiTabReady
  // This saves ~1-2 seconds by overlapping SDK load with election
  useEffect(() => {
    if (!isAuthenticated || !webPlaybackEnabled) {
      return;
    }
    // Start SDK load early - it's idempotent, so safe to call multiple times
    // This runs in parallel with the election, saving time
    void loadSpotifyWebPlaybackSdk().catch(() => {
      // Silently fail - will retry when player initialization runs
    });
  }, [isAuthenticated, webPlaybackEnabled]);

  useEffect(() => {
    const coordinator = multiTabCoordinatorRef.current;
    const tabId = coordinator?.getTabId() ?? null;
    const leaderTabId = coordinator?.getLeaderTabId() ?? null;
    console.info(`${MULTI_TAB_LOG_PREFIX} [amITheLeader]`, isLeader ? 'yes' : 'no', {
      role,
      tabId,
      leaderTabId,
      leaderSessionId,
      multiTabEnabled,
    });
  }, [isLeader, leaderSessionId, multiTabEnabled, role]);

  const syncPlaybackFromApi = useCallback(async (reason: string) => {
    console.info(`${MULTI_TAB_LOG_PREFIX} api-sync:start`, {
      reason,
      leader: isLeaderRef.current
    });
    try {
      const playback = await playerService.fetchPlaybackState();
      const deviceIdFromApi = (playback?.device as { id?: string })?.id ?? null;
      const currentDeviceId = deviceIdRef.current;
      const allowApiVolumeOnLeader =
        !isLeaderRef.current ||
        (deviceIdFromApi && currentDeviceId && deviceIdFromApi === currentDeviceId);
      if (
        isLeaderRef.current &&
        !allowApiVolumeOnLeader &&
        typeof playback?.device?.volume_percent === 'number'
      ) {
        console.info(`${MULTI_TAB_LOG_PREFIX} api-sync:skip-volume`, {
          reason,
          deviceIdFromApi,
          currentDeviceId,
          volumePercent: playback.device.volume_percent,
        });
      }
      if (!playback || !playback.item) {
        console.info(`${MULTI_TAB_LOG_PREFIX} api-sync:no-item`, {
          reason,
          hasPlayback: Boolean(playback),
          isPlaying: playback?.is_playing ?? null,
          progressMs: playback?.progress_ms ?? null,
          deviceId: deviceIdFromApi,
        });
        return;
      }
      const apiVolumePercent =
        typeof playback?.device?.volume_percent === 'number' ? playback.device.volume_percent : null;
      console.info(`${MULTI_TAB_LOG_PREFIX} api-sync:payload`, {
        reason,
        deviceIdFromApi,
        currentDeviceId,
        apiVolumePercent,
        allowApiVolumeOnLeader,
        isLeader: isLeaderRef.current,
      });
      const repeatState = playback.repeat_state as 'off' | 'context' | 'track' | undefined;
      const payload: PlayerStateBroadcast = {
        trackUri: typeof playback.item.uri === 'string' ? playback.item.uri : null,
        positionMs: typeof playback.progress_ms === 'number' ? playback.progress_ms : 0,
        durationMs: typeof playback.item.duration_ms === 'number' ? playback.item.duration_ms : null,
        paused: playback.is_playing === false,
        deviceId: deviceIdFromApi ?? deviceIdRef.current,
        volume: allowApiVolumeOnLeader
          ? typeof playback.device?.volume_percent === 'number'
            ? playback.device.volume_percent / 100
            : null
          : null,
        shuffle: typeof playback.shuffle_state === 'boolean' ? playback.shuffle_state : null,
        repeatMode: repeatState ?? null,
        albumName:
          typeof (playback.item as any)?.album?.name === 'string'
            ? (playback.item as any).album.name
            : null,
        trackName: typeof playback.item?.name === 'string' ? playback.item.name : null,
        artists:
          Array.isArray(playback.item?.artists) && playback.item.artists.length
            ? playback.item.artists.map((artist: { name?: string; id?: string | null }) => ({
                name: typeof artist?.name === 'string' ? artist.name : '',
                id: artist?.id ?? null,
              }))
            : undefined,
        sentAt: Date.now(),
      };
      applySyntheticPlayerState(payload, { allowLeader: true, source: reason });
      const coordinator = multiTabCoordinatorRef.current;
      if (isLeaderRef.current && coordinator) {
        coordinator.broadcastPlayerState({
          ...payload,
          sentAt: Date.now(),
        });
        console.info(`${MULTI_TAB_LOG_PREFIX} api-sync:broadcast`, {
          reason,
          trackUri: payload.trackUri,
          positionMs: payload.positionMs,
          deviceId: payload.deviceId ?? null
        });
      } else {
        console.info(`${MULTI_TAB_LOG_PREFIX} api-sync:applied-local-only`, {
          reason,
          leader: isLeaderRef.current
        });
      }
      console.info(`${MULTI_TAB_LOG_PREFIX} api-sync:applied`, {
        reason,
        trackUri: payload.trackUri,
        positionMs: payload.positionMs,
        deviceId: payload.deviceId ?? null
      });
    } catch (error) {
      if (isDevelopment) {
        console.warn(`${SYNC_LOG_PREFIX} api-sync-failed`, {
          reason,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }, [applySyntheticPlayerState]);

  const startLeaderHeartbeat = useCallback(() => {
    if (!multiTabEnabled) return;
    if (!isLeaderRef.current) return;
    const coordinator = multiTabCoordinatorRef.current;
    if (!coordinator) return;
    const existing = window.setInterval(() => {
      coordinator.heartbeat();
    }, 5000);
    return () => {
      window.clearInterval(existing);
    };
  }, [multiTabEnabled]);

  useEffect(() => {
    if (!isLeader) return;
    setLocalPlaybackAuthority(true);
    const player = playerRef.current;
    if (player) {
      if (isDevelopment) {
        console.info(`${MULTI_TAB_LOG_PREFIX} leader-state:request-sync`, {
          reason: 'on-leader-change'
        });
      }
      requestPlayerStateRef.current?.('sync');
    }
    // Always try API sync to populate UI even before SDK is ready.
    void syncPlaybackFromApi('leader-sync');
    const stopHeartbeat = startLeaderHeartbeat();
    return () => {
      stopHeartbeat?.();
    };
  }, [isLeader, startLeaderHeartbeat, syncPlaybackFromApi]);

  const resolvePendingDeviceWaiters = useCallback((id: string) => {
    if (!deviceWaitersRef.current.size) return;
    const waiters = Array.from(deviceWaitersRef.current);
    deviceWaitersRef.current.clear();
    waiters.forEach(({ resolve }) => {
      try {
        resolve(id);
      } catch (err) {
        if (isDevelopment) {
          console.warn('Failed resolving device waiter', err);
        }
      }
    });
  }, []);

  const rejectPendingDeviceWaiters = useCallback((err: Error) => {
    if (!deviceWaitersRef.current.size) return;
    const waiters = Array.from(deviceWaitersRef.current);
    deviceWaitersRef.current.clear();
    waiters.forEach(({ reject }) => {
      try {
        reject(err);
      } catch (error) {
        if (isDevelopment) {
          console.warn('Failed rejecting device waiter', error);
        }
      }
    });
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (typeof navigator === 'undefined' || !(navigator as { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> } }).wakeLock) {
      return;
    }
    if (wakeLockSentinelRef.current) {
      return;
    }
    try {
      const sentinel = await (navigator as any).wakeLock.request('screen');
      wakeLockSentinelRef.current = sentinel;
      wakeLockRequestedRef.current = true;
      sentinel.addEventListener?.('release', () => {
        wakeLockSentinelRef.current = null;
      });
    } catch (err) {
      if (isDevelopment) {
        console.debug('[WakeLock] request failed', err);
      }
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (!wakeLockSentinelRef.current) {
      return;
    }
    try {
      await wakeLockSentinelRef.current.release();
    } catch (err) {
      if (isDevelopment) {
        console.debug('[WakeLock] release failed', err);
      }
    } finally {
      wakeLockSentinelRef.current = null;
    }
  }, []);

  const primePlayerElement = useCallback(async () => {
    const player = playerRef.current as (Spotify.Player & { activateElement?: () => Promise<void> }) | null;
    if (!player || typeof player.activateElement !== 'function') {
      return;
    }
    if (playerPrimedRef.current) {
      return;
    }
    try {
      await player.activateElement();
      playerPrimedRef.current = true;
    } catch (err) {
      if (isDevelopment) {
        console.warn('[Playback] Failed to activate audio element', err);
      }
    }
  }, []);

  const clearPlayerStatePolling = useCallback(() => {
    if (playerStatePollingRef.current != null) {
      if (typeof window !== 'undefined') {
        window.clearInterval(playerStatePollingRef.current);
      }
      playerStatePollingRef.current = null;
    }
    playerStatePollInFlightRef.current = false;
  }, []);

  const enqueuePlaybackCommand = useCallback((task: () => Promise<void>) => {
    const chain = playbackCommandLockRef.current
      .catch(() => {})
      .then(task);
    playbackCommandLockRef.current = chain;
    return chain;
  }, []);

  const clearQueueState = useCallback(() => {
    queueRevisionRef.current = null;
    queueActionInFlightRef.current = false;
    resetAutoAdvanceState('clear-queue-state');
    currentIntentRef.current = null;
    currentPlayingRef.current = null;
    endHandledSeqRef.current = null;
    pauseIntentActiveRef.current = false;
    lastPauseIntentAtRef.current = 0;
    lastAutoResumeAttemptAtRef.current = 0;
    clearAutoResumeTimer();
    setQueueState({
      session: null,
      items: [],
      status: 'idle',
      error: null,
      lastUpdatedAt: null,
    });
    setCurrentTrackId(null);
    setIsPlaying(false);
  }, [clearAutoResumeTimer, resetAutoAdvanceState]);

  const triggerAutoAdvance = useCallback(
    (reason: string) => {
      const currentSnapshot = currentPlayingRef.current;
      if (!currentSnapshot) {
        return;
      }
      if (endHandledSeqRef.current === currentSnapshot.seq) {
        return;
      }
      endHandledSeqRef.current = currentSnapshot.seq;
      armedSeqRef.current = null;
      clearEndCandidateTimer();
      const perform = performQueueActionRef.current;
      if (!perform) {
        autoAdvanceLog('jump-skip', { seq: currentSnapshot.seq, reason, cause: 'no-performer' });
        return;
      }
      const session = queueSessionRef.current;
      const items = queueItemsRef.current;
      let baseIndex = currentSnapshot.index;
      if ((baseIndex == null || baseIndex < 0 || baseIndex >= items.length) && currentSnapshot.uri) {
        baseIndex = findQueueIndexByUri(items, currentSnapshot.uri);
      }
      if (baseIndex == null || baseIndex < 0 || baseIndex >= items.length) {
        queueDiagnostics.logWarn('auto-next:skipped', {
          reason,
          seq: currentSnapshot.seq,
          cause: 'unknown-base-index',
          queueSize: items.length,
        });
        autoAdvanceLog('jump-skip', {
          seq: currentSnapshot.seq,
          reason,
          cause: 'unknown-base-index',
          queueSize: items.length,
        });
        return;
      }
      const nextIndex = baseIndex + 1;
      if (nextIndex >= items.length) {
        queueDiagnostics.logInfo('auto-next:skipped', {
          reason,
          seq: currentSnapshot.seq,
          cause: 'end-of-queue',
          baseIndex,
          queueSize: items.length,
        });
        autoAdvanceLog('jump-skip', {
          seq: currentSnapshot.seq,
          reason,
          cause: 'end-of-queue',
          baseIndex,
          queueSize: items.length,
        });
        return;
      }
      const pointerIndex =
        typeof session?.currentItemIndex === 'number' ? session.currentItemIndex : null;
      const pointerItemId = session?.currentItemId ?? null;
      queueDiagnostics.logInfo('auto-next:trigger', {
        reason,
        seq: currentSnapshot.seq,
        baseIndex,
        nextIndex,
        pointerIndex,
        pointerItemId,
        queueSessionId: session?.id ?? null,
      });
      autoAdvanceLog('jump-dispatch', {
        reason,
        seq: currentSnapshot.seq,
        baseIndex,
        nextIndex,
        pointerIndex,
        pointerItemId,
      });
      void perform('jump-to-index', {
        origin: 'system',
        targetIndex: nextIndex,
        debugContext: {
          reason,
          seq: currentSnapshot.seq,
          baseIndex,
          nextIndex,
          pointerIndex,
          pointerItemId,
        },
      });
      requestPlayerStateRef.current('sync');
    },
    [clearEndCandidateTimer, queueDiagnostics]
  );

  const maybeConfirmEndWithPoll = useCallback(
    async (seq: number) => {
      if (!ENABLE_AUTO_END_POLL) {
        return true;
      }
      autoAdvanceLog('poll-request', { seq });
      await new Promise((resolve) => {
        setTimeout(resolve, AUTO_END_POLL_DELAY_MS);
      });
      try {
        const playback = await playerService.fetchPlaybackState();
        if (!playback || playback.is_playing === false) {
          autoAdvanceLog('poll-result', {
            seq,
            state: playback?.is_playing ?? false,
            trackUri: playback?.item?.uri ?? null,
          });
          return true;
        }
        const playbackUri: string | null =
          typeof playback?.item?.uri === 'string' ? playback.item.uri : null;
        const snapshot = currentPlayingRef.current;
        if (snapshot && playbackUri && playbackUri !== snapshot.uri) {
          autoAdvanceLog('poll-result', {
            seq,
            state: 'track-changed',
            trackUri: playbackUri,
          });
          return true;
        }
        autoAdvanceLog('poll-result', {
          seq,
          state: 'playing-same-track',
          trackUri: playbackUri ?? null,
        });
        return false;
      } catch (err) {
        queueDiagnostics.logWarn('auto-next:poll-error', {
          seq,
          message: err instanceof Error ? err.message : String(err),
        });
        autoAdvanceLog('poll-error', {
          seq,
          message: err instanceof Error ? err.message : String(err),
        });
        return true;
      }
    },
    [queueDiagnostics]
  );

  const confirmAndTriggerEnd = useCallback(
    async (seq: number, reason: AutoAdvanceReason) => {
      if (endHandledSeqRef.current === seq) {
        autoAdvanceLog('end-skip', { seq, reason, cause: 'already-handled' });
        return;
      }
      if (armedSeqRef.current !== seq) {
        autoAdvanceLog('end-skip', { seq, reason, cause: 'not-armed', armedSeq: armedSeqRef.current });
        return;
      }
      if (Date.now() < suppressEndUntilRef.current) {
        autoAdvanceLog('end-skip', {
          seq,
          reason,
          cause: 'suppressed',
          remainingMs: suppressEndUntilRef.current - Date.now(),
        });
        return;
      }
      const confirmed = await maybeConfirmEndWithPoll(seq);
      if (!confirmed) {
        queueDiagnostics.logInfo('auto-next:poll-rejected', {
          seq,
          reason,
        });
        autoAdvanceLog('end-poll-rejected', { seq, reason });
        return;
      }
       autoAdvanceLog('end-confirmed', { seq, reason });
      triggerAutoAdvance(reason);
    },
    [maybeConfirmEndWithPoll, queueDiagnostics, triggerAutoAdvance]
  );

  const scheduleAutoAdvanceCandidate = useCallback(
    (seq: number, reason: AutoAdvanceReason) => {
      if (endCandidateTimerRef.current != null) {
        clearTimeout(endCandidateTimerRef.current);
      }
      pendingEndCandidateRef.current = { seq, reason };
      queueDiagnostics.logInfo('auto-next:candidate', {
        seq,
        reason,
      });
      autoAdvanceLog('end-candidate', { seq, reason });
      endCandidateTimerRef.current = setTimeout(() => {
        endCandidateTimerRef.current = null;
        const pending = pendingEndCandidateRef.current;
        if (!pending || pending.seq !== seq || pending.reason !== reason) {
          return;
        }
        pendingEndCandidateRef.current = null;
        void confirmAndTriggerEnd(seq, reason);
      }, AUTO_END_DEBOUNCE_MS);
    },
    [confirmAndTriggerEnd, queueDiagnostics]
  );

  const resolveIntentViaApi = useCallback(
    async (seq: number) => {
      const intent = currentIntentRef.current;
      if (!intent || intent.seq !== seq || intent.started) {
        return;
      }
      autoAdvanceLog('play-resolve-fetch', { seq });
      try {
        const playback = await playerService.fetchPlaybackState();
        const trackUri =
          typeof playback?.item?.uri === 'string' ? (playback.item.uri as string) : null;
        const linkedFromUri =
          typeof playback?.item?.linked_from?.uri === 'string'
            ? (playback.item.linked_from.uri as string)
            : null;
        const durationMs =
          typeof playback?.item?.duration_ms === 'number' ? playback.item.duration_ms : null;
        const trackTitle =
          typeof playback?.item?.name === 'string' ? (playback.item.name as string) : null;
        const matched = tryArmIntentWithTrack(
          intent,
          { trackUri, linkedFromUri, durationMs, trackTitle },
          { source: 'api' }
        );
        if (!matched) {
          autoAdvanceLog('play-resolve-failed', {
            seq,
            trackUri,
            linkedFromUri,
            durationMs,
            trackTitle,
          });
        }
      } catch (err) {
        autoAdvanceLog('play-resolve-error', {
          seq,
          message: err instanceof Error ? err.message : String(err),
        });
      } finally {
        if (pendingIntentResolutionSeqRef.current === seq) {
          pendingIntentResolutionSeqRef.current = null;
        }
      }
    },
    [tryArmIntentWithTrack]
  );

  const maybeBroadcastPlayerState = useCallback(
    (state: Spotify.PlaybackState, options?: { force?: boolean }) => {
      if (!multiTabEnabled) return;
      if (!isLeaderRef.current) return;
      const coordinator = multiTabCoordinatorRef.current;
      if (!coordinator) return;
      const now = Date.now();
      const lastSent = lastPlayerBroadcastAtRef.current || 0;
      const force = options?.force ?? false;
      if (!force && lastSent && now - lastSent < PLAYER_STATE_BROADCAST_INTERVAL_MS) {
        return;
      }
      const track = state.track_window.current_track ?? null;
      const repeatMode =
        state.repeat_mode === 1
          ? 'track'
          : state.repeat_mode === 2
            ? 'context'
            : 'off';
      const queueSession = queueSessionRef.current;
      const payload: PlayerStateBroadcast = {
        trackUri: track?.uri ?? null,
        positionMs:
          typeof state.position === 'number' && Number.isFinite(state.position)
            ? Math.max(0, Math.floor(state.position))
            : 0,
        durationMs:
          typeof state.duration === 'number' && Number.isFinite(state.duration)
            ? Math.max(0, Math.floor(state.duration))
            : null,
        paused: state.paused,
        deviceId: deviceIdRef.current,
        volume:
          typeof (state as any)?.device?.volume_percent === 'number'
            ? ((state as any).device.volume_percent as number) / 100
            : null,
        shuffle: typeof state.shuffle === 'boolean' ? state.shuffle : null,
        repeatMode,
        queueSessionId: queueSession?.id ?? null,
        currentItemId: queueSession?.currentItemId ?? null,
        currentItemIndex: queueSession?.currentItemIndex ?? null,
        revision: queueSession?.revision ?? null,
        albumName: track?.album?.name ?? null,
        trackName: track?.name ?? null,
        artists: track?.artists?.map((artist) => ({
          name: artist?.name ?? '',
          id: (artist as { id?: string })?.id ?? null,
        })),
      };

      lastPlayerBroadcastAtRef.current = now;
      coordinator.broadcastPlayerState(payload);
    },
    [multiTabEnabled]
  );

    const handlePlayerStateUpdate = useCallback(
      (state: Spotify.PlaybackState, source: 'event' | 'poll' | 'sync') => {
        if (!state) return;

      const currentTrack = state.track_window.current_track ?? null;
      const trackId = currentTrack?.id ?? null;
      const currentTrackUri = currentTrack?.uri ?? null;
      const previousTrackId = previousTrackIdRef.current;
      const previousSdkTrack = state.track_window.previous_tracks?.[0] ?? null;
      const previousSdkTrackId = previousSdkTrack?.id ?? null;
      const previousSdkTrackUri = previousSdkTrack?.uri ?? null;
      const trackChanged = Boolean(trackId && previousTrackId && trackId !== previousTrackId);

      const queueSession = queueSessionRef.current;
      const queueItems = queueItemsRef.current;
      const queuePointerIndex =
        queueSession && Number.isFinite(queueSession.currentItemIndex) ? queueSession.currentItemIndex : null;
      const pointerItem =
        queuePointerIndex != null && queuePointerIndex >= 0 && queuePointerIndex < queueItems.length
          ? queueItems[queuePointerIndex]
          : null;
      const pointerItemId = queueSession?.currentItemId ?? pointerItem?.id ?? null;
      const pointerTrackUri = resolveQueueItemTrackUri(pointerItem);
      const pointerCanonicalTrackUri = pointerItem?.trackUri ?? null;
      const pointerTrackTitle =
        typeof pointerItem?.metadata === 'object' && pointerItem?.metadata
          ? (pointerItem.metadata as Record<string, unknown>).title ?? null
          : null;
      const currentTrackTitle = typeof currentTrack?.name === 'string' ? currentTrack.name : null;

      const resolvedPositionMs =
        typeof state.position === 'number' && Number.isFinite(state.position)
          ? Math.max(0, Math.floor(state.position))
          : null;
      const resolvedDurationMs =
        typeof state.duration === 'number' && Number.isFinite(state.duration)
          ? Math.max(0, Math.floor(state.duration))
          : null;

      const now = Date.now();
      const sdkTimestamp =
        typeof state.timestamp === 'number' && Number.isFinite(state.timestamp) ? state.timestamp : null;

      const lastProgressGetter = getLastNonZeroProgressSummaryRef.current;
      const lastProgressSummary = lastProgressGetter ? lastProgressGetter() : null;
      const lastProgressUpdatedAtGetter = getLastNonZeroProgressUpdatedAtRef.current;
      const lastProgressUpdatedAt = lastProgressUpdatedAtGetter ? lastProgressUpdatedAtGetter() : null;

      const deviceVolume = (state as any)?.device?.volume_percent;
      if (typeof deviceVolume === 'number') {
        console.info(`${MULTI_TAB_LOG_PREFIX} sdk-volume`, {
          source,
          deviceVolume,
          isLeader: isLeaderRef.current,
          deviceId: deviceIdRef.current ?? null,
        });
      }

      logPlaybackTimingEvent('player_state_changed', {
        source,
        paused: state.paused,
        positionMs: resolvedPositionMs,
        durationMs: resolvedDurationMs,
        sdkTimestamp,
        timestampDeltaMs: sdkTimestamp != null ? now - sdkTimestamp : null,
        nowMs: now,
        currentTrackId: trackId,
        currentTrackUri,
        previousTrackId,
        previousTrackUri: previousSdkTrackUri,
        previousTrackWindowId: previousSdkTrackId,
        trackChanged,
        pointerItemId,
        pointerTrackUri,
        pointerIndex: queuePointerIndex,
        queueRevision: queueSession?.revision ?? null,
        queueProgressMs: typeof queueSession?.currentProgressMs === 'number' ? queueSession.currentProgressMs : null,
        queueUpdatedAtIso: queueSession?.updatedAt ?? null,
        lastProgressItemId: lastProgressSummary?.itemId ?? null,
        lastProgressMs: lastProgressSummary?.progressMs ?? null,
        lastProgressUpdatedAtMs: typeof lastProgressUpdatedAt === 'number' ? lastProgressUpdatedAt : null,
        contextUri: state.context?.uri ?? null,
        shuffle: typeof state.shuffle === 'boolean' ? state.shuffle : null,
        repeatMode: typeof state.repeat_mode === 'number' ? state.repeat_mode : null,
        playbackId: (state as Spotify.PlaybackState & { playback_id?: string }).playback_id ?? null,
        deviceVolumePercent: typeof deviceVolume === 'number' ? deviceVolume : null,
        disallows: state.disallows ?? null,
      });

      if (trackChanged) {
        queueDiagnostics.logInfo('player-track-transition', {
          source,
          currentTrackUri,
          previousTrackUri: previousSdkTrackUri ?? null,
          paused: state.paused,
          positionMs: resolvedPositionMs,
          durationMs: resolvedDurationMs,
          queuePointerIndex,
          pointerItemId,
        });
      }

      const normalizedCurrentTrackUri = normalizeTrackUri(currentTrackUri);
      const normalizedPointerUri = normalizeTrackUri(pointerTrackUri);
      if (normalizedCurrentTrackUri && normalizedPointerUri && normalizedCurrentTrackUri !== normalizedPointerUri) {
        queueDiagnostics.logWarn('pointer:mismatch', {
          source,
          currentTrackUri,
          pointerTrackUri,
          pointerCanonicalTrackUri,
          queuePointerIndex,
          pointerItemId,
          queueRevision: queueSession?.revision ?? null,
          lastProgressItemId: lastProgressSummary?.itemId ?? null,
          lastProgressMs: lastProgressSummary?.progressMs ?? null,
          currentTrackTitle,
          pointerTrackTitle,
        });
        autoAdvanceLog('pointer-mismatch', {
          source,
          currentTrackUri,
          pointerTrackUri,
          queuePointerIndex,
          pointerItemId,
          currentTrackTitle,
          pointerTrackTitle,
        });
      }

      dispatch(spotifyActions.syncPlaybackState({ state }));
      setPlayerState(state);
      playerStateRef.current = state;
      setIsPlaying(!state.paused);

      const nowTs = Date.now();
      const activeIntent = currentIntentRef.current;
      if (activeIntent && !activeIntent.started) {
        if (!state.paused) {
          activeIntent.firstSeenAt = activeIntent.firstSeenAt ?? nowTs;
        }
        const canMarkStarted =
          !state.paused &&
          ((resolvedPositionMs != null && resolvedPositionMs > 0) ||
            (activeIntent.firstSeenAt != null && nowTs - activeIntent.firstSeenAt >= 350));
        if (canMarkStarted) {
          const matched = tryArmIntentWithTrack(
            activeIntent,
            {
              trackUri: currentTrackUri,
              linkedFromUri:
                typeof currentTrack?.linked_from?.uri === 'string'
                  ? currentTrack.linked_from.uri
                  : null,
              durationMs:
                typeof currentTrack?.duration_ms === 'number'
                  ? currentTrack.duration_ms
                  : resolvedDurationMs,
              trackTitle: typeof currentTrack?.name === 'string' ? currentTrack.name : null,
            },
            { source }
          );
          if (!matched && pendingIntentResolutionSeqRef.current !== activeIntent.seq) {
            pendingIntentResolutionSeqRef.current = activeIntent.seq;
            activeIntent.resolutionRequested = true;
            autoAdvanceLog('play-resolve-request', {
              seq: activeIntent.seq,
              reason: 'uri-mismatch',
              trackUri: currentTrackUri ?? null,
              trackTitle: typeof currentTrack?.name === 'string' ? currentTrack.name : null,
            });
            void resolveIntentViaApi(activeIntent.seq);
          }
        }
      }

      const playingSnapshot = currentPlayingRef.current;
      if (!playingSnapshot && currentTrackUri) {
        currentPlayingRef.current = {
          seq: activeIntent?.seq ?? playSequenceRef.current,
          uri: currentTrackUri,
          index: findQueueIndexByUri(queueItems, currentTrackUri),
          startedAt: nowTs,
        };
        if (!state.paused) {
          armedSeqRef.current = currentPlayingRef.current.seq;
          clearEndCandidateTimer();
          autoAdvanceLog('play-armed-external', {
            seq: currentPlayingRef.current.seq,
            uri: currentTrackUri,
            source,
            reason: 'no-snapshot',
            trackTitle: typeof currentTrack?.name === 'string' ? currentTrack.name : null,
          });
        }
      } else if (
        playingSnapshot &&
        currentTrackUri &&
        currentTrackUri !== playingSnapshot.uri
      ) {
        let handledByIntent = false;
        if (activeIntent && !activeIntent.started) {
          const matched = tryArmIntentWithTrack(
            activeIntent,
            {
              trackUri: currentTrackUri,
              linkedFromUri:
                typeof currentTrack?.linked_from?.uri === 'string'
                  ? currentTrack.linked_from.uri
                  : null,
              durationMs:
                typeof currentTrack?.duration_ms === 'number'
                  ? currentTrack.duration_ms
                  : resolvedDurationMs,
              trackTitle: typeof currentTrack?.name === 'string' ? currentTrack.name : null,
            },
            { source }
          );
          handledByIntent = matched;
        }
        if (handledByIntent) {
          currentIntentRef.current = null;
        } else {
        const derivedSeq = playSequenceRef.current + 1;
        playSequenceRef.current = derivedSeq;
        currentPlayingRef.current = {
          seq: derivedSeq,
          uri: currentTrackUri,
          index: findQueueIndexByUri(queueItems, currentTrackUri),
          startedAt: nowTs,
        };
        currentIntentRef.current = null;
        endHandledSeqRef.current = null;
        if (!state.paused) {
          armedSeqRef.current = derivedSeq;
          clearEndCandidateTimer();
          autoAdvanceLog('play-armed-external', {
            seq: derivedSeq,
            uri: currentTrackUri,
            source,
            reason: 'external-transition',
            trackTitle: typeof currentTrack?.name === 'string' ? currentTrack.name : null,
          });
        }
        }
      }

      setCurrentTrackId(trackId ?? null);

      const normalizedSnapshotUri = normalizeTrackUri(currentPlayingRef.current?.uri ?? null);
      const snapshotMatchesCurrent = Boolean(
        normalizedCurrentTrackUri &&
        normalizedSnapshotUri &&
        normalizedSnapshotUri === normalizedCurrentTrackUri
      );

      if (trackId && previousTrackIdRef.current !== trackId) {
        dispatch(spotifyActions.fetchLikedSong(trackId));
        previousTrackIdRef.current = trackId;
        if (!snapshotMatchesCurrent) {
          resetAutoAdvanceState('track-transition');
        } else {
          autoAdvanceLog('state-retained', {
            reason: 'track-transition-aligned',
            trackId,
            snapshotSeq: currentPlayingRef.current?.seq ?? null,
          });
        }
        const primaryArtist = currentTrack?.artists?.[0]?.name;
        document.title = primaryArtist
          ? `${currentTrack?.name ?? ''} • ${primaryArtist}`
          : currentTrack?.name ?? 'Spotify Web Player';
      } else if (!trackId) {
        previousTrackIdRef.current = null;
        resetAutoAdvanceState('track-cleared');
        document.title = 'Spotify Web Player';
      }

      if (typeof deviceVolume === 'number') {
        setVolumePercentState(deviceVolume);
        if (deviceVolume > 0) {
          lastNonZeroVolumeRef.current = deviceVolume;
        }
      }

      if (!state.paused) {
        const positionMs = resolvedPositionMs ?? 0;
        lastPlayingStateRef.current = {
          positionMs,
          durationMs: resolvedDurationMs ?? null,
          trackUri: currentTrackUri ?? null,
          updatedAt: Date.now(),
        };
      }

      const currentSnapshot = currentPlayingRef.current;
      if (currentSnapshot) {
        const isSuppressed = Date.now() < suppressEndUntilRef.current;
        const isArmed = armedSeqRef.current === currentSnapshot.seq;
        const prevMatches = previousSdkTrackUri
          ? normalizeTrackUri(previousSdkTrackUri) === normalizeTrackUri(currentSnapshot.uri)
          : false;
        const currentMatches =
          normalizeTrackUri(currentTrackUri) === normalizeTrackUri(currentSnapshot.uri);
        const candidatePausedZeroRaw = state.paused && resolvedPositionMs === 0;
        const candidateUriChangedRaw = Boolean(currentTrackUri && currentTrackUri !== currentSnapshot.uri);
        const candidateSignal = candidatePausedZeroRaw || candidateUriChangedRaw;
        if (candidateSignal) {
          if (!prevMatches) {
            autoAdvanceLog('end-guard-prev-mismatch', {
              seq: currentSnapshot.seq,
              snapshotUri: currentSnapshot.uri,
              currentTrackUri: currentTrackUri ?? null,
              previousSdkTrackUri: previousSdkTrackUri ?? null,
              statePaused: state.paused,
              positionMs: resolvedPositionMs ?? null,
            });
          }
          if (!isArmed) {
            autoAdvanceLog('end-guard-not-armed', {
              seq: currentSnapshot.seq,
              snapshotUri: currentSnapshot.uri,
              currentTrackUri: currentTrackUri ?? null,
            });
          } else if (isSuppressed) {
            autoAdvanceLog('end-guard-suppressed', {
              seq: currentSnapshot.seq,
              remainingMs: suppressEndUntilRef.current - Date.now(),
            });
          } else if (endHandledSeqRef.current === currentSnapshot.seq) {
            autoAdvanceLog('end-guard-already-handled', {
              seq: currentSnapshot.seq,
            });
          }
        }

        if (!state.paused && currentMatches && isArmed) {
          clearEndCandidateTimer();
        }

        if (isArmed && !isSuppressed && endHandledSeqRef.current !== currentSnapshot.seq) {
          const pausedZero = candidatePausedZeroRaw;
          const uriChanged = candidateUriChangedRaw;

          if (pausedZero || uriChanged) {
            let passesDurationHint = true;
            if (pausedZero) {
              const lastPlaying = lastPlayingStateRef.current;
              if (
                lastPlaying &&
                lastPlaying.trackUri === currentSnapshot.uri &&
                typeof lastPlaying.durationMs === 'number'
              ) {
                passesDurationHint =
                  lastPlaying.positionMs >=
                  Math.max(0, lastPlaying.durationMs - AUTO_END_NEAR_DURATION_MS);
              }
            }

            if (passesDurationHint) {
              scheduleAutoAdvanceCandidate(currentSnapshot.seq, pausedZero ? 'paused-zero' : 'uri-changed');
            } else {
              queueDiagnostics.logInfo('auto-next:skipped', {
                reason: 'duration-hint',
                seq: currentSnapshot.seq,
                lastPositionMs: lastPlayingStateRef.current?.positionMs ?? null,
                lastDurationMs: lastPlayingStateRef.current?.durationMs ?? null,
              });
              autoAdvanceLog('end-skip', {
                seq: currentSnapshot.seq,
                reason: 'duration-hint',
                lastPositionMs: lastPlayingStateRef.current?.positionMs ?? null,
                lastDurationMs: lastPlayingStateRef.current?.durationMs ?? null,
              });
            }
          }
        }
      }

      maybeBroadcastPlayerState(state, {
        force: source !== 'poll' || trackChanged || state.paused,
      });
    },
    [
      dispatch,
      resolveIntentViaApi,
      tryArmIntentWithTrack,
      clearEndCandidateTimer,
      queueDiagnostics,
      resetAutoAdvanceState,
      scheduleAutoAdvanceCandidate,
      setCurrentTrackId,
      setIsPlaying,
      setPlayerState,
      setVolumePercentState,
      maybeBroadcastPlayerState,
    ]
  );
  const requestPlayerState = useCallback(
    (source: 'poll' | 'sync') => {
      const player = playerRef.current;
      if (!player) {
        return;
      }
      if (playerStatePollInFlightRef.current) {
        return;
      }

      playerStatePollInFlightRef.current = true;
      player
        .getCurrentState()
        .then((state) => {
          if (state) {
            handlePlayerStateUpdate(state, source);
          }
        })
        .catch((error) => {
          if (import.meta.env?.DEV) {
            console.warn('[PlaybackProvider] player state refresh failed', error);
          }
        })
        .finally(() => {
          playerStatePollInFlightRef.current = false;
        });
    },
    [handlePlayerStateUpdate]
  );
  requestPlayerStateRef.current = requestPlayerState;

  const startPlayerStatePolling = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (playerStatePollingRef.current != null) {
      return;
    }

    playerStatePollingRef.current = window.setInterval(() => {
      requestPlayerState('poll');
    }, PLAYER_STATE_POLL_INTERVAL_MS);
  }, [requestPlayerState]);
  const updateQueueFromEnvelope = useCallback(
    (envelope: QueueSessionEnvelope) => {
      queueRevisionRef.current = envelope.session.revision;
      queueDiagnostics.logEnvelope('update', envelope);
      const { pointerItem } = getPointerFromSession(envelope.session, envelope.items);
      const pointerTrackId = pointerItem ? resolveTrackIdFromUri(pointerItem.trackUri) : null;

      setQueueState({
        session: envelope.session,
        items: envelope.items,
        status: 'ready',
        error: null,
        lastUpdatedAt: Date.now(),
      });

      setCurrentTrackId(pointerTrackId ?? null);
      setIsPlaying(envelope.session.status === 'active');
    },
    [queueDiagnostics]
  );

  const hydrateQueueSession = useCallback(async () => {
    if (!isAuthenticated) {
      clearQueueState();
      return;
    }

    if (queueHydratePromiseRef.current) {
      console.info('[DeviceInitTiming] Queue hydration already in progress, waiting', {
        timestamp: new Date().toISOString(),
      });
      await queueHydratePromiseRef.current;
      return;
    }

    const hydratePromise = (async () => {
      const hydrateStartTime = Date.now();
      console.info('[DeviceInitTiming] Queue hydration start', {
        timestamp: new Date().toISOString(),
      });
      setQueueState((prev) => ({
        ...prev,
        status: 'hydrating',
        error: null,
      }));

      try {
        const fetchStartTime = Date.now();
        const envelope = await fetchQueueSession();
        const fetchDuration = Date.now() - fetchStartTime;
        console.info('[DeviceInitTiming] Queue session fetched', {
          timestamp: new Date().toISOString(),
          durationMs: fetchDuration,
        });
        updateQueueFromEnvelope(envelope);
        const totalHydrateDuration = Date.now() - hydrateStartTime;
        console.info('[DeviceInitTiming] Queue hydration complete', {
          timestamp: new Date().toISOString(),
          totalDurationMs: totalHydrateDuration,
        });
      } catch (err) {
        const backendError = err as BackendError | undefined;
        const statusCode = typeof backendError?.status === 'number' ? backendError.status : undefined;
        const totalHydrateDuration = Date.now() - hydrateStartTime;

        if (statusCode === 404) {
          console.info('[DeviceInitTiming] Queue hydration complete - no session (404)', {
            timestamp: new Date().toISOString(),
            totalDurationMs: totalHydrateDuration,
          });
          if (isDevelopment) {
            console.info('[Queue] No active session found during hydrate.');
          }
          clearQueueState();
          return;
        }

        if (statusCode === 410) {
          if (isDevelopment) {
            console.info('[Queue] Session marked stale during hydrate.');
          }
          notifyStaleQueue();
          clearQueueState();
          return;
        }

        const payloadMessage =
          typeof backendError?.payload === 'object' &&
          backendError?.payload !== null &&
          'error' in (backendError.payload as Record<string, unknown>) &&
          typeof (backendError.payload as { error?: { message?: string } }).error?.message === 'string'
            ? (backendError.payload as { error?: { message?: string } }).error?.message
            : undefined;
        const message =
          payloadMessage ??
          (backendError instanceof Error ? backendError.message : 'Failed to hydrate queue session.');

        if (isDevelopment) {
          console.warn('[Queue] Hydration failed', {
            status: statusCode,
            payload: backendError?.payload,
            error: backendError,
          });
        }

        setQueueState((prev) => ({
          ...prev,
          status: 'idle',
          error: message,
        }));
      } finally {
        queueHydratePromiseRef.current = null;
      }
    })();

    queueHydratePromiseRef.current = hydratePromise;
    await hydratePromise;
  }, [clearQueueState, isAuthenticated, notifyStaleQueue, updateQueueFromEnvelope]);

  const handleQueueError = useCallback(
    async (operation: string, err: unknown) => {
      const backendError = err as BackendError | undefined;
      const statusCode = typeof backendError?.status === 'number' ? backendError.status : undefined;

      if (statusCode === 409) {
        queueDiagnostics.logWarn('queue:error', {
          operation,
          status: statusCode,
          reason: 'revision-conflict',
        });
        if (isDevelopment) {
          console.info(`[Queue] ${operation} hit revision conflict. Rehydrating session.`);
        }
        await hydrateQueueSession();
        return true;
      }

      if (statusCode === 410) {
        queueDiagnostics.logWarn('queue:error', {
          operation,
          status: statusCode,
          reason: 'stale-session',
        });
        if (isDevelopment) {
          console.info(`[Queue] ${operation} reported stale session. Clearing queue state.`);
        }
        notifyStaleQueue();
        clearQueueState();
        return true;
      }

      const payloadMessage =
        typeof backendError?.payload === 'object' &&
        backendError?.payload !== null &&
        'error' in (backendError.payload as Record<string, unknown>) &&
        typeof (backendError.payload as { error?: { message?: string } }).error?.message === 'string'
          ? (backendError.payload as { error?: { message?: string } }).error?.message
          : undefined;
      const message =
        payloadMessage ??
        (backendError instanceof Error ? backendError.message : 'Queue request failed.');

      queueDiagnostics.logWarn('queue:error', {
        operation,
        status: statusCode ?? null,
        reason: 'unhandled',
        message,
      });

      if (isDevelopment) {
        console.warn(`[Queue] ${operation} failed`, {
          status: statusCode,
          payload: backendError?.payload,
          error: backendError,
        });
      }

      setQueueState((prev) => ({
        ...prev,
        status: prev.session ? 'ready' : 'idle',
        error: message,
      }));

      return false;
    },
    [clearQueueState, hydrateQueueSession, notifyStaleQueue, queueDiagnostics]
  );

  const activeSessionId = queueState.session?.id ?? null;

  const handleQueueRealtimeEvent = useCallback(
    (event: QueueRealtimeEvent) => {
      queueDiagnostics.logRealtime(event);
      if (event.event === 'SESSION_STATUS' && event.session.status === 'stale') {
        if (isDevelopment) {
          console.info('[Queue] Session marked stale via realtime event. Clearing local queue state.', {
            eventSessionId: event.sessionId,
          });
        }
        notifyStaleQueue();
        clearQueueState();
        return;
      }

      if (activeSessionId && event.sessionId !== activeSessionId && isDevelopment) {
        console.info('[Queue] Realtime event reported new session id; adopting server state.', {
          expectedSessionId: activeSessionId,
          eventSessionId: event.sessionId,
          event: event.event,
        });
      }

      updateQueueFromEnvelope({ session: event.session, items: event.items });
    },
    [activeSessionId, clearQueueState, notifyStaleQueue, queueDiagnostics, updateQueueFromEnvelope]
  );

  const realtimeSubscriptionEnabled = Boolean(isAuthenticated && activeSessionId && queueUserId);

  useEffect(() => {
    if (DISABLE_HEAVY_FEATURES && isDevelopment) {
      console.info('[Queue] Realtime disabled via REACT_APP_DISABLE_HEAVY_FEATURES');
    }
  }, []);

  useQueueRealtimeSubscription({
    sessionId: activeSessionId,
    userId: queueUserId,
    enabled: realtimeSubscriptionEnabled && !DISABLE_HEAVY_FEATURES,
    onEvent: handleQueueRealtimeEvent,
    onReconnect: () => {
      queueDiagnostics.logInfo('realtime:reconnect');
      if (isDevelopment) {
        console.info('[Queue] Realtime connection re-established; rehydrating session.');
      }
      void hydrateQueueSession();
    },
    onError: (err) => {
      queueDiagnostics.logWarn('realtime:error', {
        message: err instanceof Error ? err.message : String(err),
      });
      if (isDevelopment) {
        console.warn('[Queue] Realtime subscription error', err);
      }
    },
  });


const getQueueItemForIntent = (
  intent: PlaybackIntent,
  items: QueueItem[]
): { item: QueueItem | null; index: number | null } => {
  if (!items.length) {
    return { item: null, index: null };
  }
  if (
    intent.index != null &&
    Number.isFinite(intent.index) &&
    intent.index >= 0 &&
    intent.index < items.length
  ) {
    return { item: items[intent.index] ?? null, index: intent.index };
  }
  const fallbackIndex = findQueueIndexByUri(items, intent.uri);
  if (fallbackIndex != null && fallbackIndex >= 0 && fallbackIndex < items.length) {
    return { item: items[fallbackIndex] ?? null, index: fallbackIndex };
  }
  return { item: null, index: null };
};

  const {
    sendProgress: sendQueueProgressRaw,
    getLastNonZeroProgressSummary,
    getLastNonZeroProgressUpdatedAt
  } = useQueueProgressReporter({
    session: queueState.session,
    items: queueState.items,
    playerState,
    deviceId,
    isPlaying,
    enabled: Boolean(
      isAuthenticated &&
        isLeader &&
        queueState.session &&
        queueState.session.status !== 'stale'
    ),
    heartbeatIntervalMs: PROGRESS_HEARTBEAT_INTERVAL_MS,
    onProgressAcknowledged: (envelope) => {
      queueDiagnostics.logEnvelope('progress-ack', envelope);
      const pointerItem =
        envelope.items[envelope.session.currentItemIndex] ?? null;
      const pointer =
        envelope.session.currentItemId ?? pointerItem?.id ?? null;
      const resolvedUri =
        pointerItem?.resolvedTrackUri ?? pointerItem?.trackUri ?? null;
      const pointerTrackTitle =
        pointerItem && typeof pointerItem.metadata === 'object' && pointerItem.metadata
          ? (pointerItem.metadata as Record<string, unknown>).title ?? null
          : null;
      autoAdvanceLog('progress-envelope', {
        sessionId: envelope.session.id,
        revision: envelope.session.revision,
        pointerItemId: pointer,
        pointerIndex: envelope.session.currentItemIndex,
        resolvedTrackUri: resolvedUri,
        pointerTrackTitle,
      });
      updateQueueFromEnvelope(envelope);
    },
  });

  useEffect(() => {
    sendQueueProgressRawRef.current = sendQueueProgressRaw;
  }, [sendQueueProgressRaw]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (unloadHandlerAttachedRef.current) return;

    const pauseIfLeader = async () => {
      if (!isLeaderRef.current) return;
      const player = playerRef.current;
      if (player) {
        try {
          await player.pause();
          return;
        } catch {
          // fall through to API pause
        }
      }
      try {
        await spotifyService.pausePlayback();
      } catch {
        // best-effort; ignore
      }
    };

    const handleBeforeUnload = () => {
      if (isLeaderRef.current && multiTabEnabled) {
        multiTabCoordinatorRef.current?.leaderGoingAway();
      }
      void pauseIfLeader();
      const session = queueSessionRef.current;
      const sessionDeviceId = session?.deviceId ?? null;
      const currentDeviceId = deviceIdRef.current;
      const canReport =
        isAuthenticatedRef.current &&
        session &&
        session.status !== 'stale' &&
        (isLeaderRef.current || localPlaybackAuthorityRef.current);

      if (canReport && sendQueueProgressRawRef.current) {
        void sendQueueProgressRawRef.current({
          force: true,
          keepalive: true,
          reason: 'before-unload',
          resolvedItemId: session.currentItemId ?? undefined,
          resolvedTrackUri: undefined
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', pauseIfLeader);
    unloadHandlerAttachedRef.current = true;

    // Intentionally keep handler until page unload; do not remove on re-render.
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', pauseIfLeader);
    };
  }, []);

  const sendQueueProgress = useCallback(
    async (options?: ProgressTriggerOptions) => {
      const reason = options?.reason ?? 'manual';
      const payload = {
        reason,
        force: Boolean(options?.force),
        keepalive: Boolean(options?.keepalive),
      };
      queueDiagnostics.logInfo('progress:attempt', payload);
      const sent = await sendQueueProgressRaw(options);
      queueDiagnostics.logInfo(sent ? 'progress:sent' : 'progress:skipped', {
        ...payload,
        result: sent ? 'sent' : 'skipped',
      });
      return sent;
    },
    [queueDiagnostics, sendQueueProgressRaw]
  );

  const resolvedTrackNotifier = useCallback(
    ({ itemId, trackUri, source }: { itemId: string; trackUri: string; source: string }) => {
      const normalizedUri = normalizeTrackUri(trackUri);
      if (!normalizedUri) {
        return;
      }
      const cached = resolvedTrackUriCacheRef.current.get(itemId);
      if (cached === normalizedUri) {
        return;
      }
      queueDiagnostics.logInfo('resolved-track:recorded', {
        itemId,
        trackUri: normalizedUri,
        source,
      });
      updateQueueItemResolvedUri(itemId, normalizedUri);
      void sendQueueProgress({
        reason: 'resolution',
        force: true,
        resolvedItemId: itemId,
        resolvedTrackUri: normalizedUri,
      });
    },
    [queueDiagnostics, sendQueueProgress, updateQueueItemResolvedUri]
  );
  resolvedTrackNotifierRef.current = resolvedTrackNotifier;

  useEffect(() => {
    queueSessionRef.current = queueState.session;
  }, [queueState.session]);

  useEffect(() => {
    queueItemsRef.current = queueState.items;
    const cache = resolvedTrackUriCacheRef.current;
    cache.clear();
    queueState.items.forEach((item) => {
      const normalized = normalizeTrackUri(item.resolvedTrackUri);
      if (normalized) {
        cache.set(item.id, normalized);
      }
    });
  }, [queueState.items]);

  useEffect(() => {
    playerStateRef.current = playerState;
  }, [playerState]);

  const playStateDisplayCandidate = useMemo(() => {
    const paused = typeof playerState?.paused === 'boolean' ? playerState.paused : null;
    if (paused !== null) {
      return { value: !paused, source: 'sdk' as const };
    }
    const sessionStatus = queueState.session?.status;
    if (sessionStatus === 'active' || sessionStatus === 'paused') {
      return { value: sessionStatus === 'active', source: 'queue-session' as const };
    }
    return { value: isPlaying, source: 'fallback' as const };
  }, [isPlaying, playerState, queueState.session?.status]);

  useEffect(() => {
    if (playStateDisplayTimerRef.current != null && typeof window !== 'undefined') {
      window.clearTimeout(playStateDisplayTimerRef.current);
      playStateDisplayTimerRef.current = null;
    }

    const next = playStateDisplayCandidate;
    const last = playStateDisplayRef.current;
    const now = Date.now();
    const sameSource = next.source === last.source;
    const allowImmediate =
      last.updatedAt === 0 ||
      next.value === last.value ||
      typeof window === 'undefined' ||
      next.source === 'sdk' ||
      sameSource;

    if (allowImmediate) {
      playStateDisplayRef.current = { value: next.value, source: next.source, updatedAt: now };
      setIsPlayingDisplay(next.value);
      return;
    }

    playStateDisplayTimerRef.current = window.setTimeout(() => {
      playStateDisplayTimerRef.current = null;
      playStateDisplayRef.current = { value: next.value, source: next.source, updatedAt: Date.now() };
      setIsPlayingDisplay(next.value);
    }, PLAY_STATE_UI_DEBOUNCE_MS);

    return () => {
      if (playStateDisplayTimerRef.current != null && typeof window !== 'undefined') {
        window.clearTimeout(playStateDisplayTimerRef.current);
        playStateDisplayTimerRef.current = null;
      }
    };
  }, [playStateDisplayCandidate]);

  useEffect(() => {
    return () => {
      clearAutoResumeTimer();
    };
  }, [clearAutoResumeTimer]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession || typeof MediaMetadata === 'undefined') {
      return;
    }
    const { pointerItem } = getPointerFromSession(queueState.session, queueState.items);
    if (!pointerItem) {
      try {
        navigator.mediaSession.metadata = null;
      } catch (error) {
        if (isDevelopment) {
          console.debug('[MediaSession] metadata clear failed', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
      return;
    }
    const metadata = extractQueueItemMetadata(pointerItem);
    const fallbackTitle = pointerItem.trackUri ?? 'Classical Music';
    const title =
      metadata.displayTitle ??
      metadata.title ??
      metadata.recordingName ??
      metadata.workName ??
      fallbackTitle;
    const artist = metadata.artists ?? metadata.performerName ?? metadata.composerName ?? undefined;
    const album = metadata.albumName ?? metadata.recordingName ?? metadata.workName ?? undefined;
    const artwork: Array<{ src: string; sizes?: string; type?: string }> = [];
    if (metadata.albumImageUrl) {
      artwork.push({
        src: metadata.albumImageUrl,
        sizes: '512x512',
        type: 'image/jpeg',
      });
    }
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title,
        artist,
        album,
        artwork: artwork.length > 0 ? artwork : undefined,
      });
    } catch (error) {
      if (isDevelopment) {
        console.debug('[MediaSession] metadata update failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }, [queueState.items, queueState.session]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession) {
      return;
    }
    try {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch (error) {
      if (isDevelopment) {
        console.debug('[MediaSession] playbackState update failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && wakeLockRequestedRef.current && !wakeLockSentinelRef.current) {
        void requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [requestWakeLock]);

  useEffect(() => {
    if (multiTabEnabled && !isLeader) {
      return;
    }
    if (isPlaying) {
      wakeLockRequestedRef.current = true;
      void requestWakeLock();
    } else {
      void releaseWakeLock();
    }
    return () => {
      void releaseWakeLock();
    };
  }, [isLeader, isPlaying, multiTabEnabled, releaseWakeLock, requestWakeLock]);

  useEffect(() => {
    getLastNonZeroProgressSummaryRef.current = getLastNonZeroProgressSummary;
  }, [getLastNonZeroProgressSummary]);

  useEffect(() => {
    getLastNonZeroProgressUpdatedAtRef.current = getLastNonZeroProgressUpdatedAt;
  }, [getLastNonZeroProgressUpdatedAt]);

  // Queue hydration is now deferred until after player is ready (see device ready event handler)
  // This prevents the 2-4s JSON.parse() from blocking the critical player initialization path
  // The queue doesn't need to be hydrated for playback to start - it can be empty initially
  // useEffect(() => {
  //   if (!isAuthenticated) {
  //     clearQueueState();
  //     return;
  //   }
  //   void hydrateQueueSession();
  // }, [clearQueueState, hydrateQueueSession, isAuthenticated]);

  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  useEffect(() => {
    authService.init().catch((err) => {
      if (isDevelopment) {
        console.warn('[PlaybackProvider] auth init failed', err);
      }
    });
  }, []);

  useEffect(() => {
    if (multiTabEnabled && (!multiTabReady || !isLeader)) {
      setStatus(multiTabReady ? 'idle' : 'initializing');
      setError(null);
      setDeviceId(null);
      deviceIdRef.current = null;
      setPlayerState(null);
      setCurrentTrackId(null);
      setIsPlaying(false);
      setVolumePercentState(null);
      playerRef.current?.disconnect();
      playerRef.current = null;
      clearPlayerStatePolling();
      spotifyService.setWebPlaybackDevice(null);
      dispatch(authActions.setPlayerLoaded({ playerLoaded: false }));
      resetAutoAdvanceState('multi-tab-follower');
      return;
    }

    if (!webPlaybackEnabled) {
      setStatus('error');
      setError('Spotify web playback is disabled.');
      setDeviceId(null);
      setPlayerState(null);
      setCurrentTrackId(null);
      setIsPlaying(false);
      setVolumePercentState(null);
      playerRef.current?.disconnect();
      playerRef.current = null;
      clearPlayerStatePolling();
      spotifyService.setWebPlaybackDevice(null);
      emitPlaybackTelemetry({ type: 'web-playback-disabled' });
      dispatch(authActions.setPlayerLoaded({ playerLoaded: false }));
      resetAutoAdvanceState('web-playback-disabled');
      return;
    }

    if (!isAuthenticated) {
      setStatus('unauthenticated');
      setDeviceId(null);
      setPlayerState(null);
      setCurrentTrackId(null);
      setIsPlaying(false);
      setVolumePercentState(null);
      playerRef.current?.disconnect();
      playerRef.current = null;
      clearPlayerStatePolling();
      spotifyService.setWebPlaybackDevice(null);
      dispatch(authActions.setPlayerLoaded({ playerLoaded: false }));
      resetAutoAdvanceState('unauthenticated');
      return;
    }

    let cancelled = false;
    let destroy: (() => void) | null = null;
    const initStartTime = Date.now();

    const initialize = async () => {
      // Start monitoring event loop blocking
      const { startEventLoopMonitoring, measureEventLoopDelay } = await import('../utils/eventLoopMonitor');
      startEventLoopMonitoring();
      
      setStatus('initializing');
      setError(null);
      emitPlaybackTelemetry({ type: 'initializing' });
      const eventLoopDelay = await measureEventLoopDelay();
      console.info('[DeviceInitTiming] Player initialization start', {
        timestamp: new Date().toISOString(),
        isLeader,
        multiTabEnabled,
        multiTabReady,
        eventLoopDelay,
      });
      void syncPlaybackFromApi('pre-sdk');

      try {
        const sdkLoadStartTime = Date.now();
        const Spotify = await loadSpotifyWebPlaybackSdk();
        const sdkLoadDuration = Date.now() - sdkLoadStartTime;
        console.info('[DeviceInitTiming] SDK loaded', {
          timestamp: new Date().toISOString(),
          durationMs: sdkLoadDuration,
        });
        if (cancelled) {
          dispatch(authActions.setPlayerLoaded({ playerLoaded: false }));
          return;
        }

        const playerCreateStartTime = Date.now();
        const player = new Spotify.Player({
          name: 'Classical Music Web Player',
          enableMediaSession: true,
          volume: 0.8,
          getOAuthToken: async (callback) => {
            const tokenStartTime = Date.now();
            try {
              traceDebug('PlaybackContext getOAuthToken requested');
              const token = await authService.getValidAccessToken();
              const tokenDuration = Date.now() - tokenStartTime;
              console.info('[DeviceInitTiming] Token obtained', {
                timestamp: new Date().toISOString(),
                durationMs: tokenDuration,
              });
              traceDebug('PlaybackContext getOAuthToken resolved', {
                token: maskToken(token)
              });
              const scopes = decodeTokenScopes(token);
              const scopeInfo = {
                scopes,
                hasStreaming: scopes?.includes('streaming') ?? false,
              };
              // Always surface this once for debugging, even if verbose logging is off.
              console.info('[PlaybackContext] Token scopes for Web Playback', scopeInfo);
              traceDebug('PlaybackContext token scopes', scopeInfo);
              callback(token);
            } catch (tokenError) {
              const tokenDuration = Date.now() - tokenStartTime;
              console.error('[DeviceInitTiming] Token failed', {
                timestamp: new Date().toISOString(),
                durationMs: tokenDuration,
                error: tokenError instanceof Error ? tokenError.message : String(tokenError),
              });
              traceWarn('PlaybackContext getOAuthToken failed', {
                error: tokenError instanceof Error ? tokenError.message : String(tokenError)
              });
              console.error('Failed to fetch Spotify token for Web Playback:', tokenError);
              setError('Failed to refresh Spotify session.');
              setStatus('error');
            }
          },
        });
        const playerCreateDuration = Date.now() - playerCreateStartTime;
        console.info('[DeviceInitTiming] Player instance created', {
          timestamp: new Date().toISOString(),
          durationMs: playerCreateDuration,
        });

        player.addListener('initialization_error', ({ message }) => {
          console.error('Spotify player initialization error:', message);
          setError(message);
          setStatus('error');
        });

        player.addListener('authentication_error', ({ message }) => {
          console.error('Spotify player authentication error:', message);
          setError(message);
          setStatus('error');
        });

        player.addListener('account_error', ({ message }) => {
          console.error('Spotify player account error:', message);
          setError(message);
          setStatus('error');
        });

        player.addListener('playback_error', ({ message }) => {
          console.error('Spotify player playback error:', message);
          setError(message);
        });

        player.addListener('autoplay_failed', () => {
          console.warn('Spotify player autoplay failed; user interaction required.');
          setError('Browser blocked Spotify playback. Press play to resume.');
          emitPlaybackTelemetry({ type: 'autoplay-failed', deviceId: deviceIdRef.current });
        });

        player.addListener('player_state_changed', (state) => {
          if (!state) return;
          handlePlayerStateUpdate(state, 'event');
        });

        player.addListener('ready', async ({ device_id }) => {
          if (cancelled) return;
          const deviceReadyStartTime = Date.now();
          const timeSinceInitStart = deviceReadyStartTime - initStartTime;
          console.info('[DeviceInitTiming] Device ready event received', {
            timestamp: new Date().toISOString(),
            timeSinceInitStartMs: timeSinceInitStart,
            deviceId: device_id,
          });
          setDeviceId(device_id);
          deviceIdRef.current = device_id;
          if (typeof window !== 'undefined') {
            (window as any).__spotifyDeviceId = device_id;
          }
          traceDebug('Web playback device ready', { deviceId: device_id });
          spotifyService.setWebPlaybackDevice(device_id);
          multiTabCoordinatorRef.current?.broadcastDeviceReady(device_id);
          resolvePendingDeviceWaiters(device_id);
          const attemptTransferToSdkDevice = async () => {
            const transferStartTime = Date.now();
            try {
              const fetchStateStartTime = Date.now();
              const playback = await playerService.fetchPlaybackState();
              const fetchStateDuration = Date.now() - fetchStateStartTime;
              const fromDeviceId = (playback?.device as { id?: string })?.id ?? null;
              const isPlaying = playback?.is_playing === true;
              const progressMs =
                typeof playback?.progress_ms === 'number' && Number.isFinite(playback.progress_ms)
                  ? playback?.progress_ms
                  : null;
              const hasItem = Boolean(playback?.item);

              console.info('[DeviceInitTiming] Transfer attempt - fetchPlaybackState complete', {
                timestamp: new Date().toISOString(),
                durationMs: fetchStateDuration,
                fromDeviceId,
                toDeviceId: device_id,
                hasItem,
                isPlaying,
                progressMs,
              });
              
              const transferCallStartTime = Date.now();
              await playerService.transferPlayback(device_id, isPlaying);
              const transferCallDuration = Date.now() - transferCallStartTime;
              const transferDuration = Date.now() - transferStartTime;
              console.info('[DeviceInitTiming] Transfer attempt complete', {
                timestamp: new Date().toISOString(),
                totalDurationMs: transferDuration,
                fetchStateDurationMs: fetchStateDuration,
                transferCallDurationMs: transferCallDuration,
              });
            } catch (err) {
              const transferDuration = Date.now() - transferStartTime;
              console.info('[DeviceInitTiming] Transfer attempt failed', {
                timestamp: new Date().toISOString(),
                durationMs: transferDuration,
                message: err instanceof Error ? err.message : String(err),
                toDeviceId: device_id,
              });
            }
          };

          // Transfer playback to this device if something is playing elsewhere
          await attemptTransferToSdkDevice();
          
          // Mark player as ready immediately after transfer
          // State will sync via API sync, background polling, and event listeners
          const totalInitDuration = Date.now() - initStartTime;
          console.info('[DeviceInitTiming] Player initialization complete - status=ready', {
            timestamp: new Date().toISOString(),
            totalDurationMs: totalInitDuration,
            deviceId: device_id,
          });
          setStatus('ready');
          setError(null);
          dispatch(authActions.setPlayerLoaded({ playerLoaded: true }));
          emitPlaybackTelemetry({ type: 'ready', deviceId: device_id });
          
          // Start background polling and sync - these handle state synchronization
          // API sync provides immediate state, background polling keeps it updated,
          // and player_state_changed events provide real-time updates
          startPlayerStatePolling();
          requestPlayerStateRef.current?.('sync');
          void syncPlaybackFromApi('player-ready');
          
          // Defer queue hydration until after player is ready
          // The 2-4s JSON.parse() blocks the event loop, so we defer it to avoid
          // blocking the critical initialization path. Queue can be empty initially.
          setTimeout(() => {
            void hydrateQueueSession();
          }, 0);
        });

        player.addListener('not_ready', ({ device_id }) => {
          console.warn('Spotify Web Playback not ready', device_id);
          if (deviceIdRef.current === device_id) {
            setDeviceId(null);
            deviceIdRef.current = null;
            if (typeof window !== 'undefined') {
              (window as any).__spotifyDeviceId = null;
            }
            traceWarn('Web playback device lost', { deviceId: device_id });
            spotifyService.setWebPlaybackDevice(null);
            rejectPendingDeviceWaiters(new Error('Spotify Web Playback device became unavailable.'));
            setStatus('initializing');
            dispatch(authActions.setPlayerLoaded({ playerLoaded: false }));
            clearPlayerStatePolling();
          }
          emitPlaybackTelemetry({ type: 'device-not-ready', deviceId: device_id });
        });

        const connectStartTime = Date.now();
        const connected = await player.connect();
        const connectDuration = Date.now() - connectStartTime;
        console.info('[DeviceInitTiming] Player connect complete', {
          timestamp: new Date().toISOString(),
          durationMs: connectDuration,
          connected,
        });
        if (!connected) {
          setError('Failed to establish connection with Spotify player.');
          setStatus('error');
          dispatch(authActions.setPlayerLoaded({ playerLoaded: false }));
          emitPlaybackTelemetry({ type: 'connection-failed' });
          player.disconnect();
          return;
        }

        playerRef.current = player;
        startPlayerStatePolling();
        requestPlayerStateRef.current?.('sync');
        void syncPlaybackFromApi('player-ready');
        destroy = () => {
          player.disconnect();
        };
      } catch (sdkError) {
        if (!cancelled) {
          console.error('Failed to initialize Spotify Web Playback SDK:', sdkError);
          const message = sdkError instanceof Error ? sdkError.message : 'Unknown Spotify SDK error';
          setError(message);
          setStatus('error');
          dispatch(authActions.setPlayerLoaded({ playerLoaded: false }));
          emitPlaybackTelemetry({ type: 'sdk-error', message });
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
      destroy?.();
      playerRef.current = null;
      deviceIdRef.current = null;
      clearPlayerStatePolling();
      rejectPendingDeviceWaiters(new Error('Spotify Web Playback player destroyed.'));
      dispatch(authActions.setPlayerLoaded({ playerLoaded: false }));
    };
  }, [
    dispatch,
    isAuthenticated,
    rejectPendingDeviceWaiters,
    resolvePendingDeviceWaiters,
    resetAutoAdvanceState,
    queueDiagnostics,
    syncPlaybackFromApi,
    webPlaybackEnabled,
    clearPlayerStatePolling,
    handlePlayerStateUpdate,
    maybeBroadcastPlayerState,
    startPlayerStatePolling,
    isLeader,
    multiTabEnabled,
    multiTabReady
  ]);

  const waitForDeviceId = useCallback(
    (timeoutMs: number = 7000): Promise<string> => {
      if (deviceIdRef.current) {
        return Promise.resolve(deviceIdRef.current);
      }

      return new Promise<string>((resolve, reject) => {
        let finished = false;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const waiter = {
          resolve: (id: string) => {
            if (finished) return;
            finished = true;
            if (timer) clearTimeout(timer);
            deviceWaitersRef.current.delete(waiter);
            resolve(id);
          },
          reject: (err: Error) => {
            if (finished) return;
            finished = true;
            if (timer) clearTimeout(timer);
            deviceWaitersRef.current.delete(waiter);
            reject(err);
          },
        };

        deviceWaitersRef.current.add(waiter);
        timer = setTimeout(() => {
          if (finished) return;
          finished = true;
          deviceWaitersRef.current.delete(waiter);
          reject(new Error('Timed out waiting for Spotify Web Playback device.'));
        }, timeoutMs);
      });
    },
    []
  );

  const ensureDevice = useCallback(async (options: { allowTransfer?: boolean } = {}): Promise<string> => {
    const { allowTransfer = false } = options;
    if (!webPlaybackEnabled) {
      setStatus('error');
      setError('Spotify web playback is disabled.');
      throw new Error('Spotify web playback is disabled.');
    }

    // Helper function to find and activate our device
    const ensureOurDeviceIsActive = async (expectedDeviceId: string | null): Promise<void> => {
      if (!allowTransfer) return;
      try {
        const devicesResponse = await playerService.getAvailableDevices();
        const preferredDeviceName = 'Classical Music Web Player';
        
        // First try to find device by ID if we have one
        let ourDevice = expectedDeviceId
          ? devicesResponse.devices.find(
              (device) => device.id === expectedDeviceId && device.name.toLowerCase() === preferredDeviceName.toLowerCase()
            )
          : null;
        
        // If not found by ID, try to find by name
        if (!ourDevice) {
          ourDevice = devicesResponse.devices.find(
            (device) => device.name.toLowerCase() === preferredDeviceName.toLowerCase() && !device.is_restricted
          );
        }
        
        // If found and not active, transfer playback to it
        if (ourDevice && !ourDevice.is_active && !ourDevice.is_restricted) {
          traceDebug('ensureDevice transferring to our device', { deviceId: ourDevice.id, deviceName: ourDevice.name });
          await spotifyService.transferPlayback(ourDevice.id, false);
        }
      } catch (err) {
        // If device check fails, continue - this is not critical
        traceDebug('ensureDevice device check failed', { error: err instanceof Error ? err.message : String(err) });
      }
    };

    if (deviceIdRef.current) {
      traceDebug('ensureDevice using cached device id', { deviceId: deviceIdRef.current });
      // Even with cached device ID, check if it's active and transfer if needed (when allowed)
      await ensureOurDeviceIsActive(deviceIdRef.current);
      return deviceIdRef.current;
    }

    if (!playerRef.current) {
      setStatus('initializing');
      setError('Spotify web player is still starting. Please try again in a moment.');
      emitPlaybackTelemetry({ type: 'device-not-ready', trackId: currentTrackId });
      throw new Error('Spotify Web Playback device not ready');
    }

      setStatus('initializing');
    try {
      const awaitedDeviceId = await waitForDeviceId();
      
      // After getting device ID, ensure it's active by checking available devices (when allowed)
      await ensureOurDeviceIsActive(awaitedDeviceId);
      
      setStatus('ready');
      setError(null);
      traceDebug('ensureDevice resolved new device id', { deviceId: awaitedDeviceId });
      return awaitedDeviceId;
    } catch (err) {
      console.error('Waiting for Spotify device failed:', err);
      setError('Spotify web player is still starting. Please try again in a moment.');
      emitPlaybackTelemetry({
        type: 'device-not-ready',
        trackId: currentTrackId,
        message: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  }, [currentTrackId, waitForDeviceId, webPlaybackEnabled]);

  const handleDeviceNotFoundWithGrace = useCallback(
    async (
      error: unknown,
      options: {
        actionName: string;
        retry: () => Promise<void>;
        attemptedDeviceId?: string | null;
      }
    ): Promise<boolean> => {
      const statusCode = (error as AxiosError)?.response?.status;
      if (statusCode !== 404) {
        return false;
      }

      const candidateDeviceId =
        options.attemptedDeviceId ?? deviceIdRef.current ?? spotifyService.getWebPlaybackDevice();
      const graceStart = Date.now();

      // Allow brief grace period for transient device-not-found responses to resolve on their own.
      await new Promise((resolve) => setTimeout(resolve, DEVICE_404_GRACE_MS));

      let deviceRecovered = false;
      if (candidateDeviceId) {
        try {
          const devicesResponse = await playerService.getAvailableDevices();
          deviceRecovered = devicesResponse.devices.some((d) => d.id === candidateDeviceId);
        } catch (deviceErr) {
          console.info('[Playback] device 404 grace device check failed', {
            error: deviceErr instanceof Error ? deviceErr.message : String(deviceErr),
          });
        }
      }

      if (deviceRecovered) {
        console.info('[Playback] device 404 resolved during grace window', {
          deviceId: candidateDeviceId,
          action: options.actionName,
          elapsedMs: Date.now() - graceStart,
        });
        emitPlaybackTelemetry({
          type: 'device-404-grace-recovered',
          deviceId: candidateDeviceId,
          action: options.actionName,
          elapsedMs: Date.now() - graceStart,
        });
        await options.retry();
        return true;
      }

      console.info('[Playback] device 404 persisted after grace; clearing cached device', {
        candidateDeviceId,
        action: options.actionName,
        elapsedMs: Date.now() - graceStart,
      });

      spotifyService.setWebPlaybackDevice(null);
      deviceIdRef.current = null;
      setDeviceId(null);

      const player = playerRef.current;
      if (player) {
        try {
          const reconnected = await player.connect();
          if (!reconnected) {
            console.warn('[Playback] Reconnect after device 404 did not establish connection');
          }
        } catch (connectErr) {
          console.warn('[Playback] Reconnect after device 404 failed', {
            error: connectErr instanceof Error ? connectErr.message : String(connectErr),
          });
        }
      }

      await options.retry();

      emitPlaybackTelemetry({
        type: 'device-404-grace-retry',
        deviceId: deviceIdRef.current ?? null,
        action: options.actionName,
        elapsedMs: Date.now() - graceStart,
      });

      return true;
    },
    [setDeviceId]
  );

  const skipRecording = useCallback(async (): Promise<void> => {
    try {
      const perform = performQueueActionRef.current;
      if (!perform) {
        console.warn('[skipp] performQueueActionRef missing');
        throw new Error('Queue action handler not ready');
      }
      console.info('[skipp] dispatching skip-recording');
      await perform('skip-recording');
      console.info('[skipp] skip-recording dispatched');
      emitPlaybackTelemetry({ type: 'skip-recording', deviceId, trackId: currentTrackId });
    } catch (err) {
      console.error('[skipp] Failed to skip recording via queue action:', err);
      throw err;
    }
  }, [currentTrackId, deviceId]);

  const playPointerOnSpotify = useCallback(
    async (
      source: string,
      overrides?: { session?: QueueSessionSummary | null; items?: QueueItem[]; positionMs?: number }
    ) => {
      const session = overrides?.session ?? queueSessionRef.current;
      const items = overrides?.items ?? queueItemsRef.current;
      if (!session) {
        queueDiagnostics.logInfo('spotify:play-pointer-skip', {
          source,
          reason: 'no-session',
        });
        return;
      }

      const targetDeviceId = deviceIdRef.current;
      if (session.deviceId && targetDeviceId && session.deviceId !== targetDeviceId) {
        queueDiagnostics.logInfo('spotify:play-pointer-skip', {
          source,
          reason: 'device-mismatch',
          sessionDeviceId: session.deviceId,
          deviceId: targetDeviceId,
        });
        return;
      }

      const { pointerItem, pointerIndex } = getPointerFromSession(session, items);
      const trackUri = typeof pointerItem?.trackUri === 'string' ? pointerItem.trackUri.trim() : '';
      if (!trackUri) {
        queueDiagnostics.logInfo('spotify:play-pointer-skip', {
          source,
          reason: 'missing-track',
          pointerIndex,
        });
        return;
      }
      const pointerTrackTitle =
        typeof pointerItem?.metadata === 'object' && pointerItem?.metadata
          ? (pointerItem.metadata as Record<string, unknown>).title ?? null
          : null;

      const currentPlayback = playerStateRef.current;
      const currentSdkTrackUri = currentPlayback?.track_window?.current_track?.uri ?? null;
      const previousSnapshot = currentPlayingRef.current;
      const isReplacingActiveTrack =
        previousSnapshot && previousSnapshot.uri && previousSnapshot.uri !== trackUri;
      const previousSeq = isReplacingActiveTrack ? previousSnapshot.seq : null;
      if (currentSdkTrackUri && currentSdkTrackUri === trackUri && !currentPlayback?.paused) {
        queueDiagnostics.logInfo('spotify:play-pointer-skip', {
          source,
          reason: 'already-playing',
          trackUri,
        });
        autoAdvanceLog('play-skip-existing', {
          source,
          trackUri,
          pointerIndex,
          seq: playSequenceRef.current,
          trackTitle: pointerTrackTitle,
        });
        playbackFlowLog('play-skip-existing', {
          source,
          trackUri,
          pointerIndex,
          seq: playSequenceRef.current,
          SDKPaused: currentPlayback?.paused ?? null,
          SDKPositionMs: currentPlayback?.position ?? null,
          trackTitle: pointerTrackTitle,
        });
        currentIntentRef.current = null;
        currentPlayingRef.current = {
          seq: playSequenceRef.current,
          uri: trackUri,
          index: pointerIndex ?? null,
          startedAt: Date.now(),
        };
        endHandledSeqRef.current = null;
        armedSeqRef.current = playSequenceRef.current;
        clearEndCandidateTimer();
        return;
      }

      const nextSeq = playSequenceRef.current + 1;
      playSequenceRef.current = nextSeq;
      currentIntentRef.current = {
        seq: nextSeq,
        uri: trackUri,
        index: pointerIndex ?? null,
        started: false,
        firstSeenAt: null,
        resolutionRequested: false,
      };
      suppressEndUntilRef.current = Date.now() + AUTO_END_SUPPRESS_AFTER_PLAY_MS;
      endHandledSeqRef.current = previousSeq;
      armedSeqRef.current = null;
      const body: { uris: string[]; position_ms?: number } = {
        uris: [trackUri],
      };
      const resolvedPosition =
        typeof overrides?.positionMs === 'number'
          ? overrides.positionMs
          : typeof session.currentProgressMs === 'number'
            ? session.currentProgressMs
            : null;
      if (resolvedPosition != null && resolvedPosition > 0) {
        body.position_ms = Math.max(0, Math.floor(resolvedPosition));
      }
      autoAdvanceLog('play-issued', {
        source,
        seq: nextSeq,
        pointerIndex,
        trackUri,
        positionMs: body.position_ms ?? 0,
        previousSeq,
        trackTitle: pointerTrackTitle,
      });
      playbackFlowLog('play-issued', {
        source,
        seq: nextSeq,
        pointerIndex,
        trackUri,
        positionMs: body.position_ms ?? 0,
        previousSeq,
        trackTitle: pointerTrackTitle,
      });

      const finalizeSuccessfulPlay = () => {
        queueDiagnostics.logInfo('spotify:play-pointer', {
          source,
          trackUri,
          pointerIndex,
          deviceId: deviceIdRef.current ?? null,
          positionMs: body.position_ms ?? 0,
          seq: nextSeq,
        });
        setStatus('ready');
        setError(null);
      };

      let lastDeviceIdAttempted: string | null = null;
      const performStartPlayback = async () => {
        await enqueuePlaybackCommand(async () => {
          const deviceId = await ensureDevice({ allowTransfer: true });
          lastDeviceIdAttempted = deviceId;
          await spotifyService.startPlayback(body, { deviceId });
        });
      };

      try {
        await performStartPlayback();
        finalizeSuccessfulPlay();
      } catch (error) {
        const handled404 = await handleDeviceNotFoundWithGrace(error, {
          actionName: 'play-pointer',
          retry: performStartPlayback,
          attemptedDeviceId: lastDeviceIdAttempted,
        });
        if (handled404) {
          finalizeSuccessfulPlay();
          return;
        }
        if (isSpotifyRestrictionError(error)) {
          console.info('[skipp] restriction/unavailable detected, skipping recording', {
            trackUri,
            pointerIndex,
            recordingId: pointerItem?.id ?? null,
            status: (error as AxiosError)?.response?.status ?? null,
          });
          queueDiagnostics.logWarn('spotify:play-pointer-restricted', {
            source,
            trackUri,
            pointerIndex,
            recordingId: pointerItem?.id ?? null,
          });
          emitPlaybackTelemetry({
            type: 'restricted-playback',
            trackId: pointerItem?.id ?? currentTrackId,
            deviceId: deviceIdRef.current ?? null,
            reason: 'restriction',
          });
          throw new RestrictedPlaybackError({
            trackUri,
            pointerIndex: pointerIndex ?? null,
            recordingId: pointerItem?.id ?? null,
          });
        }
        queueDiagnostics.logWarn('spotify:play-pointer-failed', {
          source,
          trackUri,
          message: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    [clearEndCandidateTimer, enqueuePlaybackCommand, ensureDevice, queueDiagnostics, setError, setStatus, currentTrackId, handleDeviceNotFoundWithGrace]
  );


  const performQueueAction = useCallback(
    async (action: QueueActionType, extras?: QueueActionExtras) => {
      if (multiTabEnabled && !isLeaderRef.current) {
        const result = await sendControlToLeader({
          kind: 'queue-action',
          action,
          extras: extras ? { ...extras } : {},
        });
        if (result.timeout) {
          promoteToLeader('control-timeout');
        } else if (!result.ok && result.error) {
          throw new Error(result.error);
        } else if (result.ok) {
          return;
        }
      }

      if (action === 'skip-recording') {
        console.info('[skipp] performQueueAction start', { action, extras });
      }
      const previousPointerIndex = queueSessionRef.current?.currentItemIndex ?? null;
      const revision = queueRevisionRef.current;
      if (revision == null) {
        playbackFlowLog('queue-action:skip', {
          action,
          reason: 'no-session',
          origin: extras?.origin ?? 'user',
        });
        if (isDevelopment) {
          console.info(`[Queue] Ignoring ${action}: no active session present.`);
        }
        return;
      }

      if (queueActionInFlightRef.current) {
        playbackFlowLog('queue-action:skip', {
          action,
          reason: 'action-in-flight',
          origin: extras?.origin ?? 'user',
        });
        if (isDevelopment) {
          console.info(`[Queue] Skipping ${action}: another action is in flight.`);
        }
        if (action === 'skip-recording') {
          console.info('[skipp] performQueueAction skipped (in-flight)');
        }
        return;
      }

      const targetIndex = extras?.targetIndex;
      const rawTargetItemIds = extras?.targetItemIds;
      const rawTargetGroupId = extras?.targetGroupId;
      const rawGroupId = extras?.groupId;
      const sourceIndex = extras?.sourceIndex;
      const targetGroupIndex = extras?.targetGroupIndex;
      const rawRecordingIds = extras?.recordingIds;
      const rawTrackUris = extras?.trackUris;
      const rawTrackDurations = extras?.trackDurationsMs;
      const rawTrackMetadata = extras?.trackMetadata;
      const normalizedItemIds =
        Array.isArray(rawTargetItemIds) && rawTargetItemIds.length
          ? rawTargetItemIds
              .map((value) => (typeof value === 'string' ? value.trim() : ''))
              .filter((value): value is string => value.length > 0)
          : [];
      const normalizedRecordingIds =
        Array.isArray(rawRecordingIds) && rawRecordingIds.length
          ? rawRecordingIds
              .map((value) => (typeof value === 'string' ? value.trim() : ''))
              .filter((value): value is string => value.length > 0)
          : [];
      const normalizedTrackUris =
        Array.isArray(rawTrackUris) && rawTrackUris.length
          ? rawTrackUris
              .map((value) => (typeof value === 'string' ? value.trim() : ''))
              .filter((value): value is string => value.length > 0)
          : [];
      const normalizedTrackDurations: Array<number | null> =
        Array.isArray(rawTrackDurations) && rawTrackDurations.length
          ? rawTrackDurations.map((value) => {
              if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
                return Math.trunc(value);
              }
              return null;
            })
          : [];
      const normalizedTrackMetadata: Array<Record<string, unknown>> =
        Array.isArray(rawTrackMetadata) && rawTrackMetadata.length
          ? rawTrackMetadata.filter((value): value is Record<string, unknown> => {
              return Boolean(value && typeof value === 'object' && !Array.isArray(value));
            })
          : [];
      const normalizedTargetGroupId =
        typeof rawTargetGroupId === 'string' && rawTargetGroupId.trim().length > 0
          ? rawTargetGroupId.trim()
          : '';
      const normalizedActionGroupId =
        typeof rawGroupId === 'string' && rawGroupId.trim().length > 0 ? rawGroupId.trim() : '';

      if (action === 'jump-to-index' && typeof targetIndex !== 'number') {
        if (isDevelopment) {
          console.warn('[Queue] jump-to-index action requires a target index.');
        }
        return;
      }
      if (action === 'remove-items' && normalizedItemIds.length === 0) {
        if (isDevelopment) {
          console.warn('[Queue] remove-items action requires at least one target item id.');
        }
        return;
      }
      if (action === 'remove-group' && !normalizedTargetGroupId) {
        if (isDevelopment) {
          console.warn('[Queue] remove-group action requires a target group id.');
        }
        return;
      }
      if (
        action === 'move-track' &&
        (!normalizedActionGroupId || typeof sourceIndex !== 'number' || typeof targetIndex !== 'number')
      ) {
        if (isDevelopment) {
          console.warn('[Queue] move-track action requires groupId, sourceIndex, and targetIndex.');
        }
        return;
      }
      if (
        action === 'move-group' &&
        (!normalizedActionGroupId || typeof targetGroupIndex !== 'number')
      ) {
        if (isDevelopment) {
          console.warn('[Queue] move-group action requires groupId and targetGroupIndex.');
        }
        return;
      }
      if (action === 'append-recordings' && normalizedRecordingIds.length === 0) {
        if (isDevelopment) {
          console.warn('[Queue] append-recordings action requires at least one recording id.');
        }
        return;
      }
      if (action === 'append-tracks' && normalizedTrackUris.length === 0) {
        if (isDevelopment) {
          console.warn('[Queue] append-tracks action requires at least one track uri.');
        }
        return;
      }

      let pendingRestrictedFollowup: { action: QueueActionType; extras?: QueueActionExtras } | null = null;
      queueActionInFlightRef.current = true;
      setQueueState((prev) => ({
        ...prev,
        status: 'hydrating',
        error: null,
      }));

      const targetDeviceId = extras?.deviceId ?? deviceIdRef.current ?? undefined;
      const origin: QueueActionOrigin = extras?.origin ?? 'user';
      const debugContext = extras?.debugContext;
      if (origin === 'user') {
        grantLocalPlaybackAuthority();
      }
      traceDebug('performQueueAction preparing payload', {
        action,
        revision,
        targetIndex,
        targetItemIds: normalizedItemIds.length ? normalizedItemIds : undefined,
        targetGroupId: normalizedTargetGroupId || undefined,
        moveGroupId: normalizedActionGroupId || undefined,
        sourceIndex,
        targetGroupIndex,
        recordingIds: normalizedRecordingIds.length ? normalizedRecordingIds : undefined,
        trackUris: normalizedTrackUris.length ? normalizedTrackUris : undefined,
        trackDurationsMs: normalizedTrackDurations.length ? normalizedTrackDurations : undefined,
        trackMetadata: normalizedTrackMetadata.length ? normalizedTrackMetadata : undefined,
        targetDeviceId: targetDeviceId ?? null,
        origin,
        debugContext,
      });
      playbackFlowLog('queue-action:start', {
        action,
        origin,
        revision,
        targetIndex: targetIndex ?? null,
        sourceIndex: sourceIndex ?? null,
        targetItemIds: normalizedItemIds.length ? normalizedItemIds : null,
        targetGroupId: normalizedTargetGroupId || null,
        moveGroupId: normalizedActionGroupId || null,
        recordingIds: normalizedRecordingIds.length ? normalizedRecordingIds : null,
        trackUris: normalizedTrackUris.length ? normalizedTrackUris : null,
        trackDurationsMs: normalizedTrackDurations.length ? normalizedTrackDurations : null,
        trackMetadata: normalizedTrackMetadata.length ? normalizedTrackMetadata : null,
        targetDeviceId: targetDeviceId ?? null,
        debugContext: debugContext ?? null,
      });
      queueDiagnostics.logInfo('queue-action:dispatch', {
        action,
        revision,
        targetIndex,
        targetItemIds: normalizedItemIds.length ? normalizedItemIds : undefined,
        targetGroupId: normalizedTargetGroupId || undefined,
        moveGroupId: normalizedActionGroupId || undefined,
        sourceIndex,
        targetGroupIndex,
        recordingIds: normalizedRecordingIds.length ? normalizedRecordingIds : undefined,
        trackUris: normalizedTrackUris.length ? normalizedTrackUris : undefined,
        trackDurationsMs: normalizedTrackDurations.length ? normalizedTrackDurations : undefined,
        trackMetadata: normalizedTrackMetadata.length ? normalizedTrackMetadata : undefined,
        targetDeviceId: targetDeviceId ?? null,
        origin,
        debugContext: debugContext ?? null,
      });

      try {
        let payload: QueueActionRequest;

        switch (action) {
          case 'jump-to-index':
            payload = {
              action,
              revision,
              origin,
              targetIndex: targetIndex as number,
              ...(targetDeviceId ? { deviceId: targetDeviceId } : {}),
            };
            break;
          case 'remove-items':
            payload = {
              action,
              revision,
              origin,
              targetItemIds: normalizedItemIds,
              ...(targetDeviceId ? { deviceId: targetDeviceId } : {}),
            };
            break;
          case 'remove-group':
            payload = {
              action,
              revision,
              origin,
              targetGroupId: normalizedTargetGroupId,
              ...(targetDeviceId ? { deviceId: targetDeviceId } : {}),
            };
            break;
          case 'move-track':
            payload = {
              action,
              revision,
              origin,
              groupId: normalizedActionGroupId,
              sourceIndex: sourceIndex as number,
              targetIndex: targetIndex as number,
              ...(targetDeviceId ? { deviceId: targetDeviceId } : {}),
            };
            break;
          case 'move-group':
            payload = {
              action,
              revision,
              origin,
              groupId: normalizedActionGroupId,
              targetGroupIndex: targetGroupIndex as number,
              ...(targetDeviceId ? { deviceId: targetDeviceId } : {}),
            };
            break;
          case 'append-recordings':
            payload = {
              action,
              revision,
              origin,
              recordingIds: normalizedRecordingIds,
              ...(targetDeviceId ? { deviceId: targetDeviceId } : {}),
            };
            break;
          case 'append-tracks':
            payload = {
              action,
              revision,
              origin,
              trackUris: normalizedTrackUris,
              ...(normalizedTrackDurations.length ? { trackDurationsMs: normalizedTrackDurations } : {}),
              ...(normalizedTrackMetadata.length ? { trackMetadata: normalizedTrackMetadata } : {}),
              ...(targetDeviceId ? { deviceId: targetDeviceId } : {}),
            };
            break;
          default:
            payload = {
              action,
              revision,
              origin,
              ...(targetDeviceId ? { deviceId: targetDeviceId } : {}),
            };
            break;
        }

        const envelope = await postQueueAction(payload);
        queueDiagnostics.logEnvelope('queue-action:success', envelope, {
          action,
          revision,
          origin,
          targetDeviceId: targetDeviceId ?? null,
          debugContext: debugContext ?? null,
        });
        autoAdvanceLog('queue-action-success', {
          action,
          origin,
          revision: envelope.session.revision,
          pointerIndex: envelope.session.currentItemIndex,
          pointerItemId: envelope.session.currentItemId,
          targetDeviceId: targetDeviceId ?? null,
          debugContext: debugContext ?? null,
        });
        playbackFlowLog('queue-action:success', {
          action,
          origin,
          revision: envelope.session.revision,
          pointerIndex: envelope.session.currentItemIndex,
          pointerItemId: envelope.session.currentItemId,
          targetDeviceId: targetDeviceId ?? null,
        });
        if (action === 'skip-recording') {
          console.info('[skipp] performQueueAction success', {
            revision: envelope.session.revision,
            pointerIndex: envelope.session.currentItemIndex,
            pointerItemId: envelope.session.currentItemId,
            items: envelope.items.length,
          });
        }
        updateQueueFromEnvelope(envelope);
        if (PLAYBACK_SYNC_ACTIONS.includes(action)) {
          try {
            await playPointerOnSpotify(`queue-action:${action}`, {
              session: envelope.session,
              items: envelope.items
            });
            if (action === 'skip-recording') {
              console.info('[skipp] playPointerOnSpotify after skip-recording');
            }
          } catch (error) {
            if (error instanceof RestrictedPlaybackError) {
              const isBackwardJump =
                action === 'jump-to-index' &&
                typeof targetIndex === 'number' &&
                typeof previousPointerIndex === 'number' &&
                targetIndex < previousPointerIndex;
              if (action === 'previous-track' || isBackwardJump) {
                const baseIndex =
                  typeof targetIndex === 'number'
                    ? targetIndex
                    : typeof previousPointerIndex === 'number'
                      ? previousPointerIndex
                      : null;
                const prevRecordingStart = findPreviousRecordingStartIndex(queueItemsRef.current, baseIndex);
                if (prevRecordingStart != null) {
                  pendingRestrictedFollowup = {
                    action: 'jump-to-index',
                    extras: { targetIndex: prevRecordingStart }
                  };
                } else {
                  pendingRestrictedFollowup = { action: 'skip-recording' };
                }
              } else {
                pendingRestrictedFollowup = { action: 'skip-recording' };
              }
            } else {
              queueDiagnostics.logWarn('spotify:play-pointer-failed', {
                source: `queue-action:${action}`,
                message: error instanceof Error ? error.message : String(error)
              });
              if (action === 'skip-recording') {
                console.warn('[skipp] playPointerOnSpotify failed after skip-recording', {
                  error: error instanceof Error ? error.message : String(error)
                });
              }
            }
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        queueDiagnostics.logWarn('queue-action:error', {
          action,
          revision,
          targetDeviceId: targetDeviceId ?? null,
          origin,
          message,
          debugContext: debugContext ?? null,
        });
        autoAdvanceLog('queue-action-error', {
          action,
          origin,
          revision,
          targetDeviceId: targetDeviceId ?? null,
          message,
          debugContext: debugContext ?? null,
        });
        playbackFlowLog('queue-action:error', {
          action,
          origin,
          revision,
          message,
          targetDeviceId: targetDeviceId ?? null,
        });
        if (action === 'skip-recording') {
          console.warn('[skipp] performQueueAction error', { message });
        }
        if (origin === 'system' && action === 'next-track') {
          emitPlaybackTelemetry({
            type: 'auto-advance-queue-action-failed',
            queueSessionId: queueSessionRef.current?.id ?? null,
            queueItemId:
              typeof debugContext === 'object' && debugContext && 'pointerItemId' in debugContext
                ? (debugContext.pointerItemId as string | null | undefined) ?? null
                : null,
            message,
          });
        }
        const handled = await handleQueueError(`action:${action}`, err);
        if (!handled) {
          throw err;
        }
      } finally {
        queueActionInFlightRef.current = false;
      }

      if (pendingRestrictedFollowup) {
        try {
          if (pendingRestrictedFollowup.action === 'jump-to-index') {
            console.info('[skipp] restricted playback on backward navigation, issuing previous recording');
            await performQueueAction('jump-to-index', {
              ...(pendingRestrictedFollowup.extras ?? {}),
              origin: extras?.origin ?? 'user'
            });
          } else {
            await skipRecording();
            console.info('[skipp] skip-recording dispatched after restricted playback');
          }
        } catch (error) {
          console.warn('[skipp] follow-up action after restricted playback failed', {
            error: error instanceof Error ? error.message : String(error),
          });
          throw error;
        }
      }
    },
    [
      grantLocalPlaybackAuthority,
      handleQueueError,
      playPointerOnSpotify,
      queueDiagnostics,
      updateQueueFromEnvelope,
      skipRecording,
      multiTabEnabled,
      promoteToLeader,
      sendControlToLeader,
    ]
  );

  performQueueActionRef.current = performQueueAction;

  const playTrack = useCallback(
    async (trackId: string, options?: PlayTrackOptions) => {
      if (multiTabEnabled && !isLeaderRef.current) {
        const result = await sendControlToLeader({
          kind: 'playback',
          action: 'play-track',
          payload: { trackId, options: options ?? null },
        });
        if (result.timeout) {
          promoteToLeader('control-timeout');
        } else if (!result.ok && result.error) {
          throw new Error(result.error);
        } else if (result.ok) {
          return;
        }
      }
      pauseIntentActiveRef.current = false;
      lastPauseIntentAtRef.current = 0;
      clearAutoResumeTimer();
      grantLocalPlaybackAuthority();

      if (!webPlaybackEnabled) {
        const message = 'Spotify web playback is disabled.';
        setError(message);
        setStatus('error');
        throw new Error(message);
      }

      const seed = deriveQueueSeed(trackId, options?.queueSeed ?? null);
      const targetTrackUri = normalizeTrackUri(options?.targetTrackUri);
      setCurrentTrackId(trackId);
      setQueueState((prev) => ({
        ...prev,
        status: 'hydrating',
        error: null,
      }));

      let id: string;
      try {
        await primePlayerElement();
        id = await ensureDevice({ allowTransfer: true });
      } catch (err) {
        console.error('Failed to prepare Spotify device for playback:', err);
        setError('Playback is unavailable right now.');
        emitPlaybackTelemetry({ type: 'playback-error', trackId, deviceId: deviceIdRef.current });
        throw err;
      }

      traceDebug('createQueueSession request', { deviceId: id, trackId });
      try {
        const envelope = await createQueueSessionApi({
          deviceId: id,
          seed,
        });

        updateQueueFromEnvelope(envelope);
        setIsPlaying(envelope.session.status === 'active');
        setStatus('ready');
        setError(null);
        emitPlaybackTelemetry({ type: 'play', trackId, deviceId: id });
        const targetIndex =
          targetTrackUri && envelope.items.length
            ? findQueueIndexByUri(envelope.items, targetTrackUri)
            : null;
        if (targetIndex != null && targetIndex !== envelope.session.currentItemIndex) {
          try {
            await performQueueAction('jump-to-index', {
              targetIndex,
              origin: 'user',
              debugContext: { source: 'play-track', targetTrackUri },
            });
            return;
          } catch (error) {
            console.warn('[Playback] Failed to jump to target track after queue creation', {
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
        let pendingRestrictedSkip = false;
        try {
          await playPointerOnSpotify('create-session', {
            session: envelope.session,
            items: envelope.items,
            positionMs: envelope.session.currentProgressMs ?? 0
          });
        } catch (error) {
          if (error instanceof RestrictedPlaybackError) {
            pendingRestrictedSkip = true;
          } else {
            throw error;
          }
        }

        if (pendingRestrictedSkip) {
          await skipRecording();
          return;
        }

        setCurrentTrackId(trackId);
        return;
      } catch (err) {
        const handled = await handleQueueError('create-session', err);
        if (!handled) {
          console.error('Failed to start queue session playback:', err);
          setError('Playback is unavailable right now.');
          emitPlaybackTelemetry({
            type: 'playback-error',
            message: err instanceof Error ? err.message : String(err),
            trackId,
            deviceId: deviceIdRef.current,
          });
          throw err;
        }
      }
    },
    [
      clearAutoResumeTimer,
      grantLocalPlaybackAuthority,
      ensureDevice,
      handleQueueError,
      playPointerOnSpotify,
      primePlayerElement,
      skipRecording,
      updateQueueFromEnvelope,
      webPlaybackEnabled,
      multiTabEnabled,
      promoteToLeader,
      sendControlToLeader,
      performQueueAction
    ]
  );

  const pauseTrack = useCallback(async () => {
    if (multiTabEnabled && !isLeaderRef.current) {
      const result = await sendControlToLeader({ kind: 'playback', action: 'pause' });
      if (result.timeout) {
        promoteToLeader('control-timeout');
      } else if (!result.ok && result.error) {
        throw new Error(result.error);
      } else if (result.ok) {
        return;
      }
    }
    pauseIntentActiveRef.current = true;
    lastPauseIntentAtRef.current = Date.now();
    clearAutoResumeTimer();
    grantLocalPlaybackAuthority();
    try {
      await spotifyService.pausePlayback();
      setIsPlaying(false);
      emitPlaybackTelemetry({ type: 'pause', trackId: currentTrackId, deviceId });
      void sendQueueProgress({ force: true, reason: 'pause' });
    } catch (err) {
      pauseIntentActiveRef.current = false;
      console.error('Failed to pause Spotify playback:', err);
      setError('Unable to pause playback.');
      emitPlaybackTelemetry({
        type: 'playback-error',
        message: err instanceof Error ? err.message : String(err),
        trackId: currentTrackId,
        deviceId,
      });
      throw err;
    }
  }, [
    clearAutoResumeTimer,
    currentTrackId,
    deviceId,
    grantLocalPlaybackAuthority,
    sendQueueProgress,
    multiTabEnabled,
    promoteToLeader,
    sendControlToLeader,
  ]);

  const resumeTrack = useCallback(async (
    options?: { session?: QueueSessionSummary | null; items?: QueueItem[]; positionMs?: number }
  ) => {
    if (multiTabEnabled && !isLeaderRef.current) {
      const result = await sendControlToLeader({
        kind: 'playback',
        action: 'resume',
        payload: { positionMs: options?.positionMs ?? null },
      });
      if (result.timeout) {
        promoteToLeader('control-timeout');
      } else if (!result.ok && result.error) {
        throw new Error(result.error);
      } else if (result.ok) {
        return;
      }
    }
    pauseIntentActiveRef.current = false;
    lastPauseIntentAtRef.current = 0;
    clearAutoResumeTimer();
    grantLocalPlaybackAuthority();
    try {
      await primePlayerElement();
      const resumePosition =
        typeof options?.positionMs === 'number'
          ? options.positionMs
          : typeof playerStateRef.current?.position === 'number'
            ? playerStateRef.current.position
            : undefined;
      let pendingRestrictedSkip = false;
      try {
        await playPointerOnSpotify('resume', {
          session: options?.session,
          items: options?.items,
          positionMs: resumePosition
        });
      } catch (error) {
        if (error instanceof RestrictedPlaybackError) {
          pendingRestrictedSkip = true;
        } else {
          throw error;
        }
      }

      if (pendingRestrictedSkip) {
        await skipRecording();
        return;
      }
      setIsPlaying(true);
      setStatus('ready');
      setError(null);
      emitPlaybackTelemetry({ type: 'resume', trackId: currentTrackId, deviceId: deviceIdRef.current });
    } catch (err) {
      console.error('Failed to resume Spotify playback:', err);
      setError('Unable to resume playback.');
      emitPlaybackTelemetry({
        type: 'playback-error',
        message: err instanceof Error ? err.message : String(err),
        trackId: currentTrackId,
        deviceId,
      });
      throw err;
    }
  }, [
    clearAutoResumeTimer,
    currentTrackId,
    deviceId,
    grantLocalPlaybackAuthority,
    playPointerOnSpotify,
    primePlayerElement,
    skipRecording,
    multiTabEnabled,
    promoteToLeader,
    sendControlToLeader,
  ]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession) {
      return;
    }
    const mediaSession = navigator.mediaSession;
    if (typeof mediaSession.setActionHandler !== 'function') {
      return;
    }

    const setActionHandler = (
      action: MediaSessionAction,
      handler: MediaSessionActionHandler | null
    ) => {
      try {
        mediaSession.setActionHandler(action, handler);
      } catch (error) {
        if (isDevelopment) {
          console.debug('[MediaSession] setActionHandler failed', {
            action,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    };

    const logAction = (action: MediaSessionAction) => {
      console.debug('[MediaSession] action', action);
    };

    const handlePlay: MediaSessionActionHandler = () => {
      logAction('play');
      void resumeTrack();
    };
    const handlePause: MediaSessionActionHandler = () => {
      logAction('pause');
      void pauseTrack();
    };
    const handleNext: MediaSessionActionHandler = () => {
      logAction('nexttrack');
      void performQueueAction('next-track', { origin: 'user' });
    };
    const handlePrevious: MediaSessionActionHandler = () => {
      logAction('previoustrack');
      void performQueueAction('previous-track', { origin: 'user' });
    };

    setActionHandler('play', handlePlay);
    setActionHandler('pause', handlePause);
    setActionHandler('stop', handlePause);
    setActionHandler('nexttrack', handleNext);
    setActionHandler('previoustrack', handlePrevious);

    return () => {
      setActionHandler('play', null);
      setActionHandler('pause', null);
      setActionHandler('stop', null);
      setActionHandler('nexttrack', null);
      setActionHandler('previoustrack', null);
    };
  }, [pauseTrack, performQueueAction, resumeTrack]);

  useEffect(() => {
    if (!ENABLE_AUTO_RESUME) {
      clearAutoResumeTimer();
      return;
    }
    if (multiTabEnabled && !isLeader) {
      clearAutoResumeTimer();
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    if (isPlaying) {
      clearAutoResumeTimer();
      lastAutoResumeAttemptAtRef.current = 0;
      return;
    }

    const playbackSnapshot = currentPlayingRef.current;
    if (!playbackSnapshot) {
      clearAutoResumeTimer();
      return;
    }

    if (pauseIntentActiveRef.current) {
      clearAutoResumeTimer();
      return;
    }

    const now = Date.now();
    if (
      lastPauseIntentAtRef.current &&
      now - lastPauseIntentAtRef.current < AUTO_RESUME_USER_PAUSE_GRACE_MS
    ) {
      clearAutoResumeTimer();
      return;
    }

    const state = playerStateRef.current;
    if (!state || !state.paused) {
      clearAutoResumeTimer();
      return;
    }

    const positionMs =
      typeof state.position === 'number' && Number.isFinite(state.position) ? state.position : null;
    const durationMs =
      typeof state.duration === 'number' && Number.isFinite(state.duration) ? state.duration : null;

    if (
      positionMs == null ||
      positionMs < AUTO_RESUME_MIN_POSITION_MS ||
      (durationMs != null &&
        durationMs > 0 &&
        positionMs >= durationMs - AUTO_RESUME_END_THRESHOLD_MS)
    ) {
      clearAutoResumeTimer();
      return;
    }

    if (
      lastAutoResumeAttemptAtRef.current &&
      now - lastAutoResumeAttemptAtRef.current < AUTO_RESUME_COOLDOWN_MS
    ) {
      return;
    }

    if (autoResumeTimerRef.current != null) {
      return;
    }

    autoResumeTimerRef.current = window.setTimeout(() => {
      autoResumeTimerRef.current = null;
      lastAutoResumeAttemptAtRef.current = Date.now();
      if (isDevelopment) {
        console.debug('[AutoResume] attempting resume', {
          positionMs,
          durationMs,
          reason: 'unexpected-pause',
        });
      }
      resumeTrack().catch((error) => {
        if (isDevelopment) {
          console.debug('[AutoResume] resume failed', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      });
    }, UNEXPECTED_PAUSE_AUTO_RESUME_DELAY_MS);
  }, [clearAutoResumeTimer, isLeader, isPlaying, multiTabEnabled, resumeTrack]);

  const seekToPosition = useCallback(
    async (positionMs: number) => {
      const safePosition = Math.max(0, Math.floor(positionMs));

      if (multiTabEnabled && !isLeaderRef.current) {
        const result = await sendControlToLeader({
          kind: 'playback',
          action: 'seek',
          payload: { positionMs: safePosition },
        });
        if (result.timeout) {
          promoteToLeader('control-timeout');
        } else if (!result.ok && result.error) {
          throw new Error(result.error);
        } else if (result.ok) {
          return;
        }
      }

      try {
        const player = playerRef.current;
        if (player) {
          await player.seek(safePosition);
        } else {
          await spotifyService.seekToPosition(safePosition);
        }

        setPlayerState((prev) =>
          prev
            ? {
                ...prev,
                position: safePosition,
              }
            : prev
        );

        emitPlaybackTelemetry({
          type: 'seek',
          trackId: currentTrackId,
          deviceId,
          positionMs: safePosition,
        });
        void sendQueueProgress({ force: true, reason: 'seek' });
      } catch (err) {
        console.error('Failed to seek Spotify playback:', err);
        setError('Unable to seek playback position.');
        emitPlaybackTelemetry({
          type: 'playback-error',
          message: err instanceof Error ? err.message : String(err),
          trackId: currentTrackId,
          deviceId,
          positionMs: safePosition,
        });
        throw err;
      }
    },
    [
      currentTrackId,
      deviceId,
      sendQueueProgress,
      multiTabEnabled,
      promoteToLeader,
      sendControlToLeader,
    ]
  );

  const setVolumePercent = useCallback(
    async (targetVolumePercent: number, options: { userInitiated?: boolean; source?: string } = {}) => {
      if (typeof targetVolumePercent !== 'number' || !Number.isFinite(targetVolumePercent)) {
        console.info(`${MULTI_TAB_LOG_PREFIX} set-volume:skip-invalid-target`, {
          target: targetVolumePercent,
          source: options.source ?? 'unknown',
          userInitiated: options.userInitiated ?? false,
          isLeader: isLeaderRef.current,
        });
        return;
      }
      const safePercent = Math.min(Math.max(Math.round(targetVolumePercent), 0), 100);
      const userInitiated = options.userInitiated === true;
      const source = options.source ?? 'unknown';
      console.info(`${MULTI_TAB_LOG_PREFIX} set-volume:call`, {
        target: targetVolumePercent,
        safePercent,
        deviceId: deviceIdRef.current ?? null,
        isLeader: isLeaderRef.current,
        multiTabEnabled,
        userInitiated,
        source,
      });

      if (multiTabEnabled && !isLeaderRef.current) {
        if (safePercent === 0 && !userInitiated) {
          console.info(`${MULTI_TAB_LOG_PREFIX} set-volume:skip-follower-zero`, {
            target: targetVolumePercent,
            safePercent,
            source,
            userInitiated,
          });
          return;
        }
        if (source === 'ui-slider' && volumePercent == null && safePercent === 0) {
          console.info(`${MULTI_TAB_LOG_PREFIX} set-volume:skip-slider-zero-uninitialized`, {
            target: targetVolumePercent,
            safePercent,
            source,
            userInitiated,
            currentVolumePercent: volumePercent,
          });
          return;
        }
        const coordinator = multiTabCoordinatorRef.current;
        const tabId = coordinator?.getTabId?.() ?? null;
        const leaderTabId = coordinator?.getLeaderTabId?.() ?? null;
        console.info(`${MULTI_TAB_LOG_PREFIX} set-volume:forward-to-leader`, {
          target: targetVolumePercent,
          safePercent,
          tabId,
          leaderTabId,
          currentVolumePercent: volumePercent,
        });
        const result = await sendControlToLeader({
          kind: 'playback',
          action: 'set-volume',
          payload: { volumePercent: safePercent },
        });
        if (result.timeout) {
          promoteToLeader('control-timeout');
        } else if (!result.ok && result.error) {
          throw new Error(result.error);
        } else if (result.ok) {
          return;
        }
      }

      try {
        const player = playerRef.current;
        if (player?.setVolume) {
          if (isDevelopment) {
            console.info(`${MULTI_TAB_LOG_PREFIX} set-volume:web-sdk`, {
              target: safePercent,
              deviceId: deviceIdRef.current ?? null,
            });
          }
          await player.setVolume(safePercent / 100);
        } else {
          const id = await ensureDevice({ allowTransfer: false });
          if (isDevelopment) {
            console.info(`${MULTI_TAB_LOG_PREFIX} set-volume:web-api`, {
              target: safePercent,
              deviceId: id ?? deviceIdRef.current ?? null,
            });
          }
          await spotifyService.setVolume(safePercent, { deviceId: id });
        }

        if (player && !deviceIdRef.current) {
          spotifyService.setVolume(safePercent).catch((err) => {
            if (isDevelopment) {
              console.debug('Set volume fallback request failed silently:', err);
            }
          });
        }

        if (safePercent > 0) {
          lastNonZeroVolumeRef.current = safePercent;
        }

        setVolumePercentState(safePercent);
        setPlayerState((prev) =>
          prev
            ? ({
                ...prev,
                device: {
                  ...((prev as { device?: Record<string, unknown> }).device ?? {}),
                  volume_percent: safePercent,
                },
              } as Spotify.PlaybackState)
            : prev
        );
      } catch (err) {
        console.error('Failed to set Spotify volume:', err);
        setError('Unable to adjust volume.');
        emitPlaybackTelemetry({
          type: 'playback-error',
          message: err instanceof Error ? err.message : String(err),
          trackId: currentTrackId,
          deviceId,
          volumePercent: safePercent,
        });
        throw err;
      }
    },
    [currentTrackId, deviceId, ensureDevice, multiTabEnabled, promoteToLeader, sendControlToLeader]
  );

  const toggleMute = useCallback(async () => {
    const currentVolume = volumePercent ?? lastNonZeroVolumeRef.current;
    const nextVolume = currentVolume > 0 ? 0 : lastNonZeroVolumeRef.current;
    await setVolumePercent(nextVolume, { userInitiated: true, source: 'toggle-mute' });
  }, [setVolumePercent, volumePercent]);

  const nextTrack = useCallback(async () => {
    try {
      await performQueueAction('next-track');
      emitPlaybackTelemetry({ type: 'next-track', deviceId, trackId: currentTrackId });
    } catch (err) {
      console.error('Failed to request next track via queue action:', err);
      throw err;
    }
  }, [currentTrackId, deviceId, performQueueAction]);

  const previousTrack = useCallback(async () => {
    const positionMs = playerStateRef.current?.position;
    const shouldRestartCurrentTrack =
      typeof positionMs === 'number' &&
      Number.isFinite(positionMs) &&
      positionMs > PREVIOUS_TRACK_RESTART_THRESHOLD_MS;

    if (shouldRestartCurrentTrack) {
      try {
        await seekToPosition(0);
        emitPlaybackTelemetry({ type: 'previous-track', deviceId, trackId: currentTrackId });
        return;
      } catch (err) {
        console.error('Restarting current track failed, falling back to previous-track action:', err);
      }
    }

    try {
      await performQueueAction('previous-track');
      emitPlaybackTelemetry({ type: 'previous-track', deviceId, trackId: currentTrackId });
    } catch (err) {
      console.error('Failed to request previous track via queue action:', err);
      throw err;
    }
  }, [currentTrackId, deviceId, performQueueAction, seekToPosition]);

  const stopPlayback = useCallback(async () => {
    try {
      await pauseTrack();
    } catch {
      // swallow pause errors during stop
    }
    setCurrentTrackId(null);
    emitPlaybackTelemetry({ type: 'stop', deviceId, trackId: currentTrackId });
  }, [currentTrackId, deviceId, pauseTrack]);

  const toggleShuffle = useCallback(
    async (_enabled?: boolean) => {
      try {
        await performQueueAction('toggle-shuffle');
        emitPlaybackTelemetry({ type: 'toggle-shuffle', deviceId, trackId: currentTrackId });
      } catch (err) {
        console.error('Failed to toggle shuffle via queue action:', err);
        throw err;
      }
    },
    [currentTrackId, deviceId, performQueueAction]
  );

  const setRepeatMode = useCallback(
    async (mode: 'track' | 'context' | 'off') => {
      if (multiTabEnabled && !isLeaderRef.current) {
        const result = await sendControlToLeader({
          kind: 'playback',
          action: 'set-repeat',
          payload: { mode },
        });
        if (result.timeout) {
          promoteToLeader('control-timeout');
        } else if (!result.ok && result.error) {
          throw new Error(result.error);
        } else if (result.ok) {
          return;
        }
      }
      try {
        await spotifyService.setRepeatMode(mode);
        emitPlaybackTelemetry({ type: 'repeat-mode', deviceId, trackId: currentTrackId, mode });
      } catch (err) {
        console.error('Failed to change repeat mode:', err);
        setError('Unable to change repeat mode.');
        emitPlaybackTelemetry({
          type: 'playback-error',
          message: err instanceof Error ? err.message : String(err),
          trackId: currentTrackId,
          deviceId,
          mode,
        });
        throw err;
      }
    },
    [currentTrackId, deviceId, multiTabEnabled, promoteToLeader, sendControlToLeader]
  );

  const startPlayback = useCallback(
    async (
      body: {
        context_uri?: string;
        uris?: string[];
        offset?: { position: number };
        position_ms?: number;
      } = {}
    ) => {
      if (multiTabEnabled && !isLeaderRef.current) {
        const result = await sendControlToLeader({
          kind: 'playback',
          action: 'start-playback',
          payload: { body },
        });
        if (result.timeout) {
          promoteToLeader('control-timeout');
        } else if (!result.ok && result.error) {
          throw new Error(result.error);
        } else if (result.ok) {
          return;
        }
      }
      grantLocalPlaybackAuthority();
      try {
        let lastDeviceIdAttempted: string | null = null;
        const runPlayback = async () => {
          const id = await ensureDevice({ allowTransfer: true });
          lastDeviceIdAttempted = id;
          clearQueueState();
          await spotifyService.startPlayback(body, { deviceId: id });
          setIsPlaying(true);
          setStatus('ready');
          setError(null);
          emitPlaybackTelemetry({ type: 'start-playback', deviceId: id, trackId: currentTrackId });
        };

        try {
          await runPlayback();
        } catch (err) {
          const handled = await handleDeviceNotFoundWithGrace(err, {
            actionName: 'start-playback',
            retry: runPlayback,
            attemptedDeviceId: lastDeviceIdAttempted,
          });
          if (handled) {
            emitPlaybackTelemetry({
              type: 'start-playback-retry',
              deviceId: deviceIdRef.current ?? null,
              trackId: currentTrackId,
            });
            return;
          }

          throw err;
        }
      } catch (err) {
        console.error('Failed to start Spotify playback:', err);
        setError('Spotify playback is unavailable right now.');
        setStatus('error');
        emitPlaybackTelemetry({
          type: 'playback-error',
          message: err instanceof Error ? err.message : String(err),
          trackId: currentTrackId,
          deviceId: deviceIdRef.current ?? null,
        });
        throw err;
      }
    },
    [
      clearQueueState,
      currentTrackId,
      ensureDevice,
      grantLocalPlaybackAuthority,
      multiTabEnabled,
      promoteToLeader,
      sendControlToLeader,
      handleDeviceNotFoundWithGrace,
    ]
  );

  const handleIncomingControl = useCallback(
    async (message: ControlMessage): Promise<{ ok: boolean; error?: string }> => {
      const { action } = message;
      if (action.kind === 'queue-action') {
        await performQueueAction(action.action as QueueActionType, action.extras as QueueActionExtras | undefined);
        return { ok: true };
      }

      switch (action.action) {
        case 'play-track':
          await playTrack(
            (action.payload as { trackId?: string; options?: PlayTrackOptions })?.trackId ?? '',
            (action.payload as { trackId?: string; options?: PlayTrackOptions })?.options
          );
          return { ok: true };
        case 'pause':
          await pauseTrack();
          return { ok: true };
        case 'resume':
          await resumeTrack({
            positionMs:
              typeof (action.payload as { positionMs?: number })?.positionMs === 'number'
                ? (action.payload as { positionMs?: number }).positionMs
                : undefined,
          });
          return { ok: true };
        case 'seek':
          await seekToPosition((action.payload as { positionMs?: number })?.positionMs ?? 0);
          return { ok: true };
        case 'set-volume': {
          const requestedVolume = (action.payload as { volumePercent?: number })?.volumePercent;
          if (typeof requestedVolume !== 'number' || !Number.isFinite(requestedVolume)) {
            if (isDevelopment) {
              console.info(`${MULTI_TAB_LOG_PREFIX} control:set-volume:skip-invalid`, {
                requestedVolume,
                requestId: message.requestId,
              });
            }
            return { ok: false, error: 'invalid-volume' };
          }
          console.info(`${MULTI_TAB_LOG_PREFIX} control:set-volume`, {
            requestedVolume,
            requestId: message.requestId,
          });
          await setVolumePercent(requestedVolume, { userInitiated: true, source: 'control' });
          return { ok: true };
        }
        case 'toggle-mute':
          await toggleMute();
          return { ok: true };
        case 'next-track':
          await nextTrack();
          return { ok: true };
        case 'previous-track':
          await previousTrack();
          return { ok: true };
        case 'skip-recording':
          await skipRecording();
          return { ok: true };
        case 'stop':
          await stopPlayback();
          return { ok: true };
        case 'toggle-shuffle':
          await toggleShuffle();
          return { ok: true };
        case 'set-repeat':
          await setRepeatMode((action.payload as { mode?: 'track' | 'context' | 'off' })?.mode ?? 'off');
          return { ok: true };
        case 'start-playback':
          await startPlayback((action.payload as { body?: Record<string, unknown> })?.body ?? {});
          return { ok: true };
        default:
          return { ok: false, error: `unknown action ${action.action}` };
      }
    },
    [
      nextTrack,
      pauseTrack,
      performQueueAction,
      playTrack,
      previousTrack,
      resumeTrack,
      seekToPosition,
      setRepeatMode,
      setVolumePercent,
      skipRecording,
      startPlayback,
      stopPlayback,
      toggleMute,
      toggleShuffle,
    ]
  );

  useEffect(() => {
    controlHandlerRef.current = handleIncomingControl;
  }, [handleIncomingControl]);

  const contextValue = useMemo<PlaybackContextType>(
    () => ({
      status,
      error,
      deviceId,
      player: playerRef.current,
      playerState,
      currentTrackId,
      isPlaying,
      isPlayingDisplay,
      volumePercent,
      queueSession: queueState.session,
      queueItems: queueState.items,
      queueStatus: queueState.status,
      queueError: queueState.error,
      role,
      isLeader,
      leaderSessionId,
      multiTabEnabled,
      hydrateQueueSession,
      clearQueueSession: clearQueueState,
      performQueueAction,
      playTrack,
      pauseTrack,
      resumeTrack,
      seekToPosition,
      setVolumePercent,
      toggleMute,
      nextTrack,
      previousTrack,
      skipRecording,
      stopPlayback,
      toggleShuffle,
      setRepeatMode,
      startPlayback,
    }),
    [
      status,
      error,
      deviceId,
      playerState,
      currentTrackId,
      isPlaying,
      isPlayingDisplay,
      volumePercent,
      queueState.session,
      queueState.items,
      queueState.status,
      queueState.error,
      role,
      isLeader,
      leaderSessionId,
      multiTabEnabled,
      hydrateQueueSession,
      clearQueueState,
      performQueueAction,
      playTrack,
      pauseTrack,
      resumeTrack,
      seekToPosition,
      setVolumePercent,
      toggleMute,
      nextTrack,
      previousTrack,
      skipRecording,
      stopPlayback,
      toggleShuffle,
      setRepeatMode,
      startPlayback,
    ]
  );

  return <PlaybackContext.Provider value={contextValue}>{children}</PlaybackContext.Provider>;
};

export const usePlayback = () => {
  const context = useContext(PlaybackContext);
  if (!context) {
    throw new Error('usePlayback must be used within a PlaybackProvider');
  }
  return context;
};

export default PlaybackContext;
